import {
	relations, sql, 
} from "drizzle-orm";
import {
	type AnyPgColumn,
	bigint,
	boolean,
	check,
	date,
	doublePrecision,
	geometry,
	index,
	integer,
	jsonb,
	pgEnum,
	pgTable,
	primaryKey,
	serial,
	text,
	timestamp,
	unique,
	uniqueIndex,
	uuid,
	varchar,
	vector,
} from "drizzle-orm/pg-core";
import { type IMetarDated } from "metar-taf-parser";

import { polygon } from "./postgis";
import { GenericEndorsementSchema } from "./schema/endorsements";
import { tsvector } from "./tsvector";
export type Scopes = "agent" | "broker" | "demo" | "fleet_manager" | "pilot" | "underwriter" | "sms_admin";
import type { CompRecommendationResult } from "@ca/types/comp_recommendations";
import type {
	CompsTarget, CompSubjectContext, CompViewDefinition,
} from "@ca/types/comps";
import type { FourEyesFeedback } from "@ca/types/four_eyes";
import type { IncidentSourceFields } from "@ca/types/incidents";
import type { PolicyMetadata } from "@ca/types/policies";
import type { RotorWingActualRiskData } from "@ca/types/rotor_wing";
import type { DecisionFollowUp, UWDEvidence } from "@ca/types/uwds";

import { Type } from "@sinclair/typebox";

import {
	type FaaAirmanRating,
} from "./schema/airmen";
import {
	type FlightEventData,
} from "./schema/flight-events";
import {
	type FoqaDebriefFlags,
	type FoqaDebriefOverrides,
	type FoqaDebriefSummary,
	type FoqaFlagConfig,
	type FoqaMeasurements,
	type FoqaScoringPolicy,
	type FoqaThresholdConfig,
} from "./schema/foqa";
import {
	type FoqaPerformanceApplicability,
	type FoqaPerformanceAxis,
	type FoqaPerformanceCells,
	type FoqaPerformanceConfig,
	type FoqaPerformanceForm,
	type FoqaPerformanceKind,
	type FoqaPerformanceReference,
	type FoqaPerformanceResult,
	type FoqaPerformanceSource,
} from "./schema/foqa-performance";
import { type AircraftVrefProfile } from "./schema/foqa-vref";
import {
	type GateSource,
	type GateState,
	type PrepDetail,
	type PrepKind,
	type PrepState,
	type PullState,
	type ReportDropReason,
} from "./schema/ledger";
import {
	type PilotRatingEntry,
} from "./schema/pilot_ratings";
import {
	type AdditionalInsured,
	type AlternateQuote,
	type CostLineItem,
	type Endorsement,
	type Party,
	type QuoteAircraft,
	type QuoteLobData,
} from "./schema/quote";
import { type QuotePrintPayload } from "./schema/quote-print";
import {
	type ExtractedSubmission,
	type SubmissionIncidents,
} from "./schema/submissions";
const updatedAt = timestamp("updated_at", { mode: "date", withTimezone: true })
	.defaultNow()
	.notNull()
	.$onUpdateFn(() => new Date());

const createdAt = timestamp("created_at", { mode: "date", withTimezone: true })
	.defaultNow()
	.notNull();

export const accounts = pgTable("accounts", {
	id: uuid().defaultRandom().primaryKey().notNull(),
	name: text("name").notNull().unique(),
	phone: text("phone"),
	streetAddress: text("street_address"),
	city: text("city"),
	state: text("state"),
	postalCode: text("postal_code"),
	country: text("country"),
	attention: text("attention"),
	createdAt,
	updatedAt,
});

export const identities = pgTable(
	"identities",
	{
		key: text("key")
			.notNull()
			.primaryKey()
			.default(sql`gen_random_uuid()`),
		name: text("name"),
		dob: date("dob", { mode: "date" }),
		phone: text("phone"),
		avatar: text("avatar"),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("identities_key_idx").on(table.key),
		index("identities_name_idx").on(table.name),
	],
);
export const identityRelations = relations(identities, ({ many }) => ({
	submissionActivities: many(submissionActivity),
	flightComments: many(flightComments),
}));
export type IdentitySchema = typeof identities.$inferSelect;

export const users = pgTable(
	"users",
	{
		createdAt,
		email: text("email")
			.unique()
			.references(() => identities.key, { onDelete: "cascade" })
			.notNull(),
		id: uuid().defaultRandom().primaryKey().notNull(),
		password: varchar({ length: 255 }).notNull(),
		scopes: text("scopes")
			.array()
			.default(sql`ARRAY[]::text[]`)
			.$type<Scopes[]>()
			.notNull(),
		settings: jsonb("settings")
			.$type<{
			riskAlertLevel: string;
			termsAcceptedDate?: string | null;
			policyChatEnabled?: boolean;
		}>()
			.default({ riskAlertLevel: "elevated" }),
		updatedAt,
		lastLogin: timestamp("last_login", { mode: "date", withTimezone: true }),
		/** Set when staff issue a temporary password; cleared by the first change or reset. */
		mustResetPassword: boolean("must_reset_password").notNull().default(false),
	},
	(table) => [index("users_email_idx").on(table.email)],
);

export const userRelations = relations(users, ({ one }) => ({
	identity: one(identities, {
		fields: [users.email],
		references: [identities.key],
	}),
}));

export const pwResetTokens = pgTable(
	"pw_reset_tokens",
	{
		createdAt,
		expiresAt: timestamp("expires_at", {
			mode: "date",
			withTimezone: true,
		}).notNull(),
		id: uuid().defaultRandom().primaryKey().notNull(),
		token: varchar({ length: 255 }).notNull(),
		updatedAt,
		userId: uuid("user_id")
			.references(() => users.id, { onDelete: "cascade" })
			.notNull(),
	},
	(table) => [
		index("pw_reset_tokens_idx").on(table.expiresAt, table.token, table.userId),
	],
);

export type UserIdentitySchema = UserSchema & { name: null | string };
export type UserSchema = Omit<typeof users.$inferSelect, "password">;

export const brokerages = pgTable(
	"brokerages",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		name: text("name").notNull(),
		website: text("website"),
		avatar: text("avatar"),
		streetAddress: text("street_address"),
		city: text("city"),
		state: text("state"),
		postalCode: text("postal_code"),
		country: text("country"),
		code: text("code").notNull(),
		displayPublicly: boolean("display_publicly").default(false).notNull(),
		defaultUnderwriter: text("default_underwriter").references(
			() => identities.key,
			{ onDelete: "set null" },
		),
		createdAt,
		updatedAt,
	},
	(table) => [unique("brokerages_code_unique").on(table.code)],
);

export const brokerageAddresses = pgTable(
	"brokerage_addresses",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		brokerageId: uuid("brokerage_id")
			.references(() => brokerages.id, { onDelete: "cascade" })
			.notNull(),
		label: text("label"),
		streetAddress: text("street_address"),
		city: text("city"),
		state: text("state"),
		postalCode: text("postal_code"),
		country: text("country"),
		isDefault: boolean("is_default").default(false).notNull(),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("brokerage_addresses_brokerage_id_idx").on(table.brokerageId),
		uniqueIndex("brokerage_addresses_default_unique")
			.on(table.brokerageId)
			.where(sql`${table.isDefault}`),
	],
);

export const brokerageAddressRelations = relations(brokerageAddresses, ({ one, many }) => ({
	brokerage: one(brokerages, {
		fields: [brokerageAddresses.brokerageId],
		references: [brokerages.id],
	}),
	brokers: many(brokers),
}));

export const brokers = pgTable(
	"brokers",
	{
		email: text("email")
			.references(() => identities.key, { onDelete: "cascade" })
			.notNull()
			.primaryKey(),
		brokerageId: uuid("brokerage_id").references(() => brokerages.id, {
			onDelete: "set null",
		}),
		brokerageAddressId: uuid("brokerage_address_id").references(() => brokerageAddresses.id, {
			onDelete: "set null",
		}),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("brokers_brokerage_id_idx").on(table.brokerageId),
		index("brokers_brokerage_address_id_idx").on(table.brokerageAddressId),
	],
);

export const brokerRelations = relations(brokers, ({ one, many }) => ({
	brokerage: one(brokerages, {
		fields: [brokers.brokerageId],
		references: [brokerages.id],
	}),
	brokerageAddress: one(brokerageAddresses, {
		fields: [brokers.brokerageAddressId],
		references: [brokerageAddresses.id],
	}),
	identity: one(identities, {
		fields: [brokers.email],
		references: [identities.key],
	}),
	submissions: many(submissions),
}));

export const leads = pgTable(
	"leads",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		// free-text tail number, not an FK to `aircraft` — a lead's aircraft may not exist in our system yet
		aircraftRegistration: text("aircraft_registration"),
		contactName: text("contact_name"),
		contactEmail: text("contact_email"),
		contactPhone: text("contact_phone"),
		broker: text("broker").references(() => brokers.email, { onDelete: "set null" }),
		notes: text("notes"),
		policyExpirationDate: date("policy_expiration_date", { mode: "string" }),
		// defaults to the creating underwriter at create-time; reassignable
		assignedUnderwriter: uuid("assigned_underwriter").references(() => users.id, { onDelete: "set null" }),
		// set when the linked submission reaches a terminal state (bound/declined/archived); archived leads are hidden from the active list
		archivedAt: timestamp("archived_at", { mode: "date", withTimezone: true }),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("leads_broker_idx").on(table.broker),
		index("leads_assigned_underwriter_idx").on(table.assignedUnderwriter),
		index("leads_aircraft_registration_idx").on(table.aircraftRegistration),
		index("leads_archived_at_idx").on(table.archivedAt),
	],
);

export const leadRelations = relations(leads, ({ one }) => ({
	aircraft: one(aircraft, {
		fields: [leads.aircraftRegistration],
		references: [aircraft.tailNumber],
	}),
	brokerRef: one(brokers, {
		fields: [leads.broker],
		references: [brokers.email],
	}),
	underwriter: one(users, {
		fields: [leads.assignedUnderwriter],
		references: [users.id],
	}),
}));

export type LeadInsertSchema = typeof leads.$inferInsert;
export type LeadSchema = typeof leads.$inferSelect;

export const compViews = pgTable(
	"comp_views",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		userId: uuid("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
		name: text("name").notNull(),
		isDefault: boolean("is_default").default(false).notNull(),
		definition: jsonb("definition").$type<CompViewDefinition>().notNull(),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("comp_views_user_id_idx").on(table.userId),
		unique("comp_views_user_id_name_unique").on(table.userId, table.name),
		uniqueIndex("comp_views_user_default_unique")
			.on(table.userId)
			.where(sql`${table.isDefault}`),
	],
);

export const compViewRelations = relations(compViews, ({ one }) => ({
	user: one(users, {
		fields: [compViews.userId],
		references: [users.id],
	}),
}));

export type CompViewSchema = typeof compViews.$inferSelect;

export const compRecommendationStatus = pgEnum("comp_recommendation_status", [
	"pending",
	"running",
	"succeeded",
	"failed",
]);

export const compRecommendations = pgTable(
	"comp_recommendations",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		submissionId: uuid("submission_id")
			.references(() => submissions.id, { onDelete: "cascade" })
			.notNull(),
		tailNumber: text("tail_number").notNull(),
		requestedBy: uuid("requested_by").references(() => users.id, { onDelete: "set null" }),
		status: compRecommendationStatus("status").default("pending").notNull(),
		error: text("error"),
		target: jsonb("target").$type<CompsTarget>().notNull(),
		subject: jsonb("subject").$type<CompSubjectContext>().notNull(),
		result: jsonb("result").$type<CompRecommendationResult>(),
		model: text("model"),
		promptVersion: text("prompt_version"),
		createdAt,
		updatedAt,
		completedAt: timestamp("completed_at", {
			mode: "date",
			withTimezone: true,
		}),
	},
	(table) => [
		index("comp_recommendations_submission_tail_created_idx").on(table.submissionId, table.tailNumber, table.createdAt),
		uniqueIndex("comp_recommendations_live_unique")
			.on(table.submissionId, table.tailNumber)
			.where(sql`${table.status} in ('pending', 'running')`),
	],
);

export type CompRecommendationSchema = typeof compRecommendations.$inferSelect;

export const submissionsLeads = pgTable(
	"submissions_leads",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		leadId: uuid("lead_id").references(() => leads.id, { onDelete: "cascade" }).notNull(),
		submissionId: uuid("submission_id").references(() => submissions.id, { onDelete: "cascade" }).notNull(),
		createdAt,
		updatedAt,
	},
	(table) => [
		unique("submissions_leads_lead_submission_unique").on(table.leadId, table.submissionId),
		index("submissions_leads_submission_id_idx").on(table.submissionId),
	],
);

export const submissionsLeadsRelations = relations(submissionsLeads, ({ one }) => ({
	lead: one(leads, {
		fields: [submissionsLeads.leadId],
		references: [leads.id],
	}),
	submission: one(submissions, {
		fields: [submissionsLeads.submissionId],
		references: [submissions.id],
	}),
}));

export type SubmissionLeadInsertSchema = typeof submissionsLeads.$inferInsert;
export type SubmissionLeadSchema = typeof submissionsLeads.$inferSelect;

export const conversationType = pgEnum("conversation_type", ["policy", "flight"]);
export type ConversationType = (typeof conversationType.enumValues)[number];

export const conversationMessageRole = pgEnum("conversation_message_role", ["assistant", "user"]);
export type ConversationMessageRole = (typeof conversationMessageRole.enumValues)[number];

export const conversations = pgTable(
	"conversations",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		userId: uuid("user_id")
			.references(() => users.id, { onDelete: "cascade" })
			.notNull(),
		type: conversationType("type").notNull(),
		referenceId: text("reference_id"),
		title: text("title").notNull(),
		archived: boolean("archived").default(false).notNull(),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("conversations_user_id_idx").on(table.userId),
		index("conversations_type_idx").on(table.type),
		index("conversations_reference_id_idx").on(table.referenceId),
	],
);

export const conversationMessages = pgTable(
	"conversation_messages",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		conversationId: uuid("conversation_id")
			.references(() => conversations.id, { onDelete: "cascade" })
			.notNull(),
		role: conversationMessageRole("role").notNull(),
		content: text("content").notNull(),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("conversation_messages_conversation_id_idx").on(table.conversationId),
		index("conversation_messages_created_at_idx").on(table.createdAt.desc()),
	],
);

export const conversationRelations = relations(conversations, ({ many, one }) => ({
	user: one(users, {
		fields: [conversations.userId],
		references: [users.id],
	}),
	messages: many(conversationMessages),
}));

export const conversationMessageRelations = relations(
	conversationMessages,
	({ one }) => ({
		conversation: one(conversations, {
			fields: [conversationMessages.conversationId],
			references: [conversations.id],
		}),
	}),
);

export type ConversationSchema = typeof conversations.$inferSelect;
export type ConversationInsertSchema = typeof conversations.$inferInsert;
export type ConversationMessageSchema = typeof conversationMessages.$inferSelect;
export type ConversationMessageInsertSchema = typeof conversationMessages.$inferInsert;

export const brokerageRelations = relations(brokerages, ({ many, one }) => ({
	addresses: many(brokerageAddresses),
	brokers: many(brokers),
	invoices: many(invoices),
	defaultUnderwriter: one(identities, {
		fields: [brokerages.defaultUnderwriter],
		references: [identities.key],
	}),
}));

export const pilots = pgTable(
	"pilots",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		identityKey: text("identity_key")
			.references(() => identities.key, { onDelete: "cascade" })
			.notNull(),
		createdAt,
		updatedAt,
	},
	(table) => [index("pilots_identity_key_idx").on(table.identityKey)],
);

// FAA "Releasable Airman Directory" reference data; standalone, not linked to identities/pilots.
export const faaAirmen = pgTable(
	"faa_airmen",
	{
		uniqueId: text("unique_id").primaryKey().notNull(),
		firstMiddleName: text("first_middle_name"),
		lastNameSuffix: text("last_name_suffix"),
		street1: text("street1"),
		street2: text("street2"),
		city: text("city"),
		state: text("state"),
		zipCode: text("zip_code"),
		country: text("country"),
		region: text("region"),
		medicalClass: text("medical_class"),
		medicalDate: date("medical_date", { mode: "string" }),
		medicalExpireDate: date("medical_expire_date", { mode: "string" }),
		basicMedCourseDate: date("basic_med_course_date", { mode: "string" }),
		basicMedCmecDate: date("basic_med_cmec_date", { mode: "string" }),
		// 'simple' config (names shouldn't stem); name weighted above address so name queries rank first.
		searchVector: tsvector("search_vector").generatedAlwaysAs(
			sql`setweight(to_tsvector('simple', coalesce(first_middle_name, '') || ' ' || coalesce(last_name_suffix, '')), 'A') ||
				setweight(to_tsvector('simple', coalesce(street1, '') || ' ' || coalesce(city, '') || ' ' || coalesce(state, '') || ' ' || coalesce(zip_code, '') || ' ' || coalesce(country, '')), 'B')`,
		),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("faa_airmen_last_name_suffix_idx").on(table.lastNameSuffix),
		index("faa_airmen_state_idx").on(table.state),
		index("faa_airmen_search_idx").using("gin", table.searchVector),
	],
);

export const faaAirmanCertificates = pgTable(
	"faa_airman_certificates",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		uniqueId: text("unique_id")
			.references(() => faaAirmen.uniqueId, { onDelete: "cascade" })
			.notNull(),
		certificateType: text("certificate_type"),
		certificateLevel: text("certificate_level"),
		certificateExpireDate: date("certificate_expire_date", { mode: "string" }),
		// Source rating slots joined verbatim (e.g. "A/ASEL|A/AMEL"), kept for fidelity.
		ratingsRaw: text("ratings_raw"),
		// Pilot ratings (e.g. ASEL, AMEL, INST).
		ratings: jsonb("ratings")
			.$type<FaaAirmanRating[]>()
			.notNull()
			.default(sql`'[]'::jsonb`),
		// Aircraft type ratings (e.g. B-737, DC-9).
		typeRatings: jsonb("type_ratings")
			.$type<FaaAirmanRating[]>()
			.notNull()
			.default(sql`'[]'::jsonb`),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("faa_airman_certificates_unique_id_idx").on(table.uniqueId),
		index("faa_airman_certificates_certificate_type_idx").on(table.certificateType),
	],
);

export const faaAirmenRelations = relations(faaAirmen, ({ many }) => ({
	certificates: many(faaAirmanCertificates),
}));

export const faaAirmanCertificateRelations = relations(
	faaAirmanCertificates,
	({ one }) => ({
		airman: one(faaAirmen, {
			fields: [faaAirmanCertificates.uniqueId],
			references: [faaAirmen.uniqueId],
		}),
	}),
);

export type FaaAirmanInsertSchema = typeof faaAirmen.$inferInsert;
export type FaaAirmanSchema = typeof faaAirmen.$inferSelect;
export type FaaAirmanCertificateInsertSchema = typeof faaAirmanCertificates.$inferInsert;
export type FaaAirmanCertificateSchema = typeof faaAirmanCertificates.$inferSelect;

export const flightState = pgEnum("flight_state", ["ground", "airborne"]);

export type FlightState = (typeof flightState.enumValues)[number];

export const aircraft = pgTable(
	"aircraft",
	{
		createdAt,
		flightState: flightState("flight_state"),
		flightStateUpdatedAt: timestamp("flight_state_updated_at", {
			mode: "date",
			withTimezone: true,
		}),
		id: uuid().defaultRandom().primaryKey().notNull(),
		ident: varchar({ length: 20 }),
		make: text("make").notNull().default(""),
		model: text("model").notNull().default(""),
		seats: varchar({ length: 20 }),
		tailNumber: varchar("tail_number", { length: 30 }).notNull().unique(),
		type: varchar("type").references(() => aircraftTypes.icaoCode, {
			onDelete: "set null",
		}),
		updatedAt,
		year: varchar({ length: 4 }),
		serialNumber: text("serial_number"),
		icaoHex: text("icao_hex"),
		active: boolean("active").default(true),
		flightawareBlocked: boolean("flightaware_blocked"),
		flightawareBlockedCheckedAt: timestamp("flightaware_blocked_checked_at", {
			mode: "date",
			withTimezone: true,
		}),
	},
	(table) => [index("tail_idx").on(table.tailNumber)],
);

export type AircraftInsertSchema = typeof aircraft.$inferInsert;
export type AircraftSchema = typeof aircraft.$inferSelect;

export const uas = pgTable("uas", {
	id: uuid().defaultRandom().primaryKey().notNull(),
	serialNumber: varchar("serial_number", { length: 30 }).notNull().unique(),
	make: text("make").notNull().default(""),
	model: text("model").notNull().default(""),
	year: varchar({ length: 4 }),
	createdAt,
	updatedAt,
});

export type UasSchema = typeof uas.$inferSelect;

export const aircraftConfigs = pgTable(
	"aircraft_configs",
	{
		aircraftId: uuid("aircraft_id").references(() => aircraft.id, { onDelete: "cascade" }).notNull(),
		key: text("key").notNull(),
		value: jsonb("value").notNull(),
		createdAt,
		updatedAt,
	},
	(table) => [primaryKey({ columns: [table.aircraftId, table.key] })],
);

export const aircraftTypes = pgTable(
	"aircraft_types",
	{
		aac: varchar({ length: 40 }),
		aacMaximum: varchar({ length: 40 }),
		aacMinimum: varchar({ length: 40 }),
		adg: varchar({ length: 40 }),
		approachSpeedKnot: integer("approach_speed_knot"),
		approachSpeedMaximumKnot: integer("approach_speed_maximum_knot"),
		approachSpeedMinimumKnot: integer("approach_speed_minimum_knot"),
		class: varchar({ length: 40 }),
		cockpitToMainGearFt: doublePrecision("cockpit_to_main_gear_ft"),
		createdAt,
		cwt: varchar({ length: 40 }),
		faaDesignator: varchar("faa_designator", { length: 40 }),
		faaRegistry: varchar("faa_registry", { length: 40 }),
		faaWeight: varchar("faa_weight", { length: 40 }),
		icaoCode: varchar("icao_code", { length: 40 }).primaryKey().notNull(),
		icaoWtc: varchar("icao_wtc", { length: 40 }),
		lahso: varchar("lahso", { length: 40 }),
		lastUpdate: timestamp("last_update", { mode: "date" }),
		lengthFt: doublePrecision("length_ft"),
		mainGearConfig: varchar("main_gear_config", { length: 40 }),
		mainGearWidthFt: doublePrecision("main_gear_width_ft"),
		malwLb: integer("malw_lb"),
		manufacturer: text("manufacturer").notNull(),
		modelBada: text("model_bada"),
		modelFaa: text("model_faa").notNull(),
		mtowLb: integer("mtow_lb"),
		numEngines: integer("num_engines"),
		oneHalfWakeCategory: varchar("one_half_wake_category", { length: 40 }),
		parkingAreaFt2: integer("parking_area_ft2"),
		physicalClassEngine: varchar("physical_class_engine", { length: 50 }),
		rotorDiameterFt: doublePrecision("rotor_diameter_ft"),
		srs: varchar({ length: 40 }),
		tailHeightAtOewFt: doublePrecision("tail_height_at_oew_ft"),
		tdg: varchar({ length: 40 }),
		twoWakeCategoryAppxA: varchar("two_wake_category_appx_a", { length: 40 }),
		twoWakeCategoryAppxB: varchar("two_wake_category_appx_b", { length: 40 }),
		updatedAt,
		wheelbaseFt: doublePrecision("wheelbase_ft"),
		wingspanFtWithoutWinglets: doublePrecision("wingspan_ft_without_winglets"),
		wingspanFtWithWinglets: doublePrecision("wingspan_ft_with_winglets"),
	},
	(table) => [
		index("aircraft_type_code_idx").on(table.icaoCode),
		unique("unique_aircraft_type_code").on(table.icaoCode),
	],
);
export type AircraftTypeSchema = typeof aircraftTypes.$inferSelect;

export type GoogleBearerToken = {
	access_token?: null | string;
	expiry_date?: null | number;
	id_token?: null | string;
	refresh_token?: null | string;
	scope?: string;
	token_type?: null | string;
};

export const googleTokens = pgTable("google_tokens", {
	createdAt,
	id: uuid().defaultRandom().primaryKey().notNull(),
	tokens: jsonb("tokens").$type<GoogleBearerToken>().notNull(),
	updatedAt,
});

export type GoogleTokenSchema = typeof googleTokens.$inferSelect;

export const submissionStatus = pgEnum("submission_status", [
	"new",
	"in_progress",
	"indicated",
	"quoted",
	"bound",
	"quoted_not_bound",
	"declined",
	"archived",
]);
export type SubmissionStatus = (typeof submissionStatus.enumValues)[number];

export const CLOSED_SUBMISSION_STATUSES = [
	"archived",
	"bound",
	"declined",
	"quoted_not_bound",
] as const;

export const OPEN_SUBMISSION_STATUSES: SubmissionStatus[] = submissionStatus.enumValues
	.filter((status) => !(CLOSED_SUBMISSION_STATUSES as readonly SubmissionStatus[]).includes(status));
export const SubmissionStatusSchema = Type.Union([
	Type.Literal("new"),
	Type.Literal("in_progress"),
	Type.Literal("indicated"),
	Type.Literal("quoted"),
	Type.Literal("bound"),
	Type.Literal("quoted_not_bound"),
	Type.Literal("declined"),
	Type.Literal("archived"),
]);

export const inboxMessages = pgTable(
	"inbox_messages",
	{
		// this is the gmail message id
		id: varchar({ length: 255 }).primaryKey().notNull(),
		threadId: varchar({ length: 255 }),
		// this is the message id from the email header
		messageId: text("message_id"),
		createdAt,
		updatedAt,
		processedAt: timestamp("processed_at", {
			mode: "date",
			withTimezone: true,
		}),
	},
	(table) => [index("inbox_messages_thread_id_idx").on(table.threadId)],
);

export const submissionLossReasons = pgEnum("submission_loss_reasons", [
	"price",
	"limits",
	"aging_pilot_concern",
	"skeptical_of_new_market",
	"unfavorable_view_of_data",
	"incumbent_loyalty",
	"unwilling_to_meet_training_requirements",
	"new_purchase_fell_through",
	"other",
]);

export const linesOfBusiness = pgEnum("lines_of_business", [
	"general_aviation",
	"product_liability",
	"commercial_general_liability",
	"uas",
]);
export type LOBEnumType = (typeof linesOfBusiness.enumValues)[number];

export const SubmissionLossReasonsSchema = Type.Union([
	Type.Literal("price"),
	Type.Literal("limits"),
	Type.Literal("aging_pilot_concern"),
	Type.Literal("skeptical_of_new_market"),
	Type.Literal("unfavorable_view_of_data"),
	Type.Literal("incumbent_loyalty"),
	Type.Literal("unwilling_to_meet_training_requirements"),
	Type.Literal("new_purchase_fell_through"),
	Type.Literal("other"),
]);

export type SubmissionLossReasons = (typeof submissionLossReasons.enumValues)[number];

export const submissionSource = pgEnum("submission_source", [
	"standard_inbox",
	"partner_api",
	"unknown",
]);

export type SubmissionSources = (typeof submissionSource.enumValues)[number];

export const submissionGeneratedFrom = pgEnum("submission_generated_from", [
	"policy_renewal",
]);

export type SubmissionGeneratedFrom = (typeof submissionGeneratedFrom.enumValues)[number];

export const submissions = pgTable(
	"submissions",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		createdAt,
		updatedAt,
		assignedTo: uuid("assigned_to").references(() => users.id, {
			onDelete: "set null",
		}),
		accountId: uuid("account_id").references(() => accounts.id, {
			onDelete: "set null",
		}),
		extracted: jsonb().$type<ExtractedSubmission | null>().default(null),
		from: varchar({ length: 255 }).notNull(),
		htmlText: text("html_text"),
		inboxMessageId: varchar("inbox_message_id", { length: 255 }).references(
			() => inboxMessages.id,
			{ onDelete: "set null" },
		),
		plainText: text("plain_text"),
		resolution: text("resolution"),
		status: submissionStatus("status").default("new").notNull(),
		resolvedAt: timestamp("resolved_at", { mode: "date", withTimezone: true }),
		quotedAt: timestamp("quoted_at", { mode: "date", withTimezone: true }),
		lossReason: submissionLossReasons("loss_reason"),
		summary: text("summary"),
		considerations: text("considerations")
			.array()
			.default(sql`ARRAY[]::text[]`)
			.$type<string[]>()
			.notNull(),
		subject: text("subject").notNull(),
		timestamp: timestamp("timestamp", {
			mode: "date",
			withTimezone: true,
		}).notNull(),
		to: varchar({ length: 255 }).notNull(),
		broker: text("broker").references(() => brokers.email, {
			onDelete: "set null",
		}),
		incidents: jsonb("incidents")
			.$type<SubmissionIncidents>()
			.default(sql`'[]'::jsonb`),
		additionalInterests: jsonb("additional_interests")
			.$type<AdditionalInsured[]>()
			.default(sql`'[]'::jsonb`),
		inceptionDate: date("inception_date", { mode: "string" }),
		isNewPurchase: boolean("is_new_purchase").default(false),
		referenceId: text("reference_id"),
		partnerSlug: text("partner_slug"),
		state: text("state"),
		linkedThreads: text("linked_threads").array().default(sql`'{}'::text[]`).notNull(),
		target: boolean("target").default(false).notNull(),
		dismissBORWarning: boolean("dismiss_bor_warning").default(false).notNull(),
		incumbentMarket: text("incumbent_market"),
		lob: linesOfBusiness("lob").default("general_aviation").notNull(),
		demoedOn: date("demoed_on", { mode: "string" }),
		opas: text("opas").array().default(sql`'{}'::text[]`).notNull(),
		source: submissionSource("source").default("unknown").notNull(),
		generatedFrom: submissionGeneratedFrom("generated_from"),
		previousSubmissionId: uuid("previous_submission_id")
			.references((): AnyPgColumn => submissions.id, { onDelete: "set null" }),
		/** Underwriter's short rationale for their end decision on this account */
		underwriterDecisionBlurb: text("underwriter_decision_blurb"),
		/** AI-generated overall description of what was done and assumed rationale leading to the final decision */
		aiDecisionRationale: text("ai_decision_rationale"),
		/** When true, submission actions (and recording uploads) may be recorded. Default false; set true for newly created submissions. */
		allowActionTracking: boolean("allow_action_tracking").default(false).notNull(),
		/** When true, week-of quote follow-up (ping) may be sent. Default true. */
		enableWeekOfFollowup: boolean("enable_week_of_followup").default(true).notNull(),
		/** When true, day-of quote follow-up (inception day email) may be sent. Default true. */
		enableDayOfFollowup: boolean("enable_day_of_followup").default(true).notNull(),
	},
	(table) => {
		return [
			index("assigned_to_idx").on(table.assignedTo),
			index("inbox_message_id_idx").on(table.inboxMessageId),
			index("timestamp_idx").on(table.timestamp.desc()),
			index("broker_idx").on(table.broker),
			index("account_id_idx").on(table.accountId),
			index("submissions_inception_date_idx").on(table.inceptionDate.desc()),
			index("submissions_ts_status_idx").on(
				table.timestamp.desc(),
				table.status,
			),
			index("submissions_reference_id_idx").on(table.referenceId),
			index("submissions_partner_slug_idx").on(table.partnerSlug),
			index("submissions_previous_submission_id_idx").on(table.previousSubmissionId),
		];
	},
);

export const citations = pgTable("citations", {
	id: uuid().defaultRandom().primaryKey().notNull(),
	citationId: text("citation_id").notNull(),
	referenceId: text("reference_id").notNull(),
	type: text("type").notNull(),
	url: text("url").notNull(),
	title: text("title").notNull(),
	createdAt,
	updatedAt,
}, (table) => [
	index("citations_citation_id_idx").on(table.citationId),
	index("citations_reference_id_idx").on(table.referenceId),
	index("citations_type_idx").on(table.type),
]);

export const submissionAircraftType = pgEnum("submission_aircraft_type", [
	"submitted",
	"historic",
]);
export type SubmissionAircraftType = (typeof submissionAircraftType.enumValues)[number];
export const submissionsAircraft = pgTable(
	"submissions_aircraft",
	{
		submissionId: uuid("submission_id")
			.references(() => submissions.id, { onDelete: "cascade" })
			.notNull(),
		aircraftId: uuid("aircraft_id")
			.references(() => aircraft.id, { onDelete: "cascade" })
			.notNull(),
		type: submissionAircraftType("type").default("submitted").notNull(),
		skiFloatValue: doublePrecision("ski_float_value"),
	},
	(table) => [primaryKey({ columns: [table.submissionId, table.aircraftId] })],
);

export const SubmissionActivityTypeSchema = Type.Union([
	Type.Literal("update_status"),
	Type.Literal("note"),
	Type.Literal("reminder"),
	Type.Literal("acknowledgement"),
	Type.Literal("dismiss_bor_warning"),
	Type.Literal("nack"),
	Type.Literal("quote_followup_ping"),
	Type.Literal("quote_followup_inception_day"),
	Type.Literal("renewal_generated"),
	Type.Literal("quote_sent"),
	Type.Literal("aircraft_added"),
	Type.Literal("aircraft_removed"),
]);
export const submissionActivityType = pgEnum("submission_activity_type", [
	"update_status",
	"note",
	"reminder",
	"acknowledgement",
	"dismiss_bor_warning",
	"nack",
	"quote_followup_ping",
	"quote_followup_inception_day",
	"renewal_generated",
	"quote_sent",
	"aircraft_added",
	"aircraft_removed",
]);

export const submissionActivity = pgTable(
	"submission_activity",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		submissionId: uuid("submission_id")
			.references(() => submissions.id, { onDelete: "cascade" })
			.notNull(),
		performedBy: text("performed_by").references(() => identities.key, {
			onDelete: "set null",
		}),
		activity: submissionActivityType("activity").notNull(),
		data: jsonb("data")
			.$type<Record<string, unknown>>()
			.notNull()
			.default(sql`'{}'::jsonb`),
		timestamp: timestamp("timestamp", {
			mode: "date",
			withTimezone: true,
		}).notNull(),
	},
	(table) => [
		index("submission_activity_submission_id_idx").on(table.submissionId),
	],
);

export const submissionComments = pgTable(
	"submission_comments",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		submissionId: uuid("submission_id")
			.references(() => submissions.id, { onDelete: "cascade" })
			.notNull(),
		comment: text("comment").notNull(),
		approvedStatus: text("approved_status"),
		author: text("author").references(() => identities.key, {
			onDelete: "set null",
		}),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("submission_comments_submission_id_idx").on(table.submissionId),
		index("submission_comments_author_idx").on(table.author),
		index("submission_comments_approved_status_idx").on(table.approvedStatus),
	],
);

export const indicationReviewStatus = pgEnum("indication_review_status", [
	"proposed",
	"approved",
	"rejected",
]);

export const submissionIndications = pgTable(
	"submission_indications",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		submissionId: uuid("submission_id")
			.references(() => submissions.id, { onDelete: "cascade" })
			.notNull(),
		price: doublePrecision("price").notNull(),
		sentiment: integer("sentiment").notNull(),
		comment: text("comment"),
		createdBy: uuid("created_by").references(() => users.id).notNull(),
		agentGenerated: boolean("agent_generated").default(false).notNull(),
		reviewerId: uuid("reviewer_id").references(() => users.id),
		reviewedAt: timestamp("reviewed_at", {
			mode: "string",
			withTimezone: true,
		}),
		reviewStatus: indicationReviewStatus("review_status").default("proposed").notNull(),
		reviewerComment: text("reviewer_comment"),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("submission_indications_submission_id_idx").on(table.submissionId),
		index("submission_indications_created_by_idx").on(table.createdBy),
		index("submission_indications_sentiment_idx").on(table.sentiment),
	],
);

export const underwritingDecisionResult = pgEnum("underwriting_decision_result", [
	"quote",
	"decline",
]);

export type UnderwritingDecisionResult = (typeof underwritingDecisionResult.enumValues)[number];

export const underwritingDecisionReviewStatus = pgEnum("underwriting_decision_review_status", [
	"pending",
	"agree",
	"disagree",
	"deferred",
	"superseded",
]);
export type UnderwritingDecisionReviewStatus = (typeof underwritingDecisionReviewStatus.enumValues)[number];

export const underwritingDecisions = pgTable(
	"underwriting_decisions",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		submissionId: uuid("submission_id")
			.references(() => submissions.id, { onDelete: "cascade" })
			.notNull(),
		quoteId: uuid("quote_id").references(() => quotes.id, { onDelete: "set null" }),
		decision: underwritingDecisionResult("decision").notNull(),
		reasoning: text("reasoning").notNull(),
		summary: text("summary").notNull(),
		evidence: jsonb("evidence").$type<UWDEvidence[]>().notNull(),
		reviewerId: uuid("reviewer_id").references(() => users.id),
		reviewerComments: text("reviewer_comments"),
		reviewedAt: timestamp("reviewed_at", {
			mode: "date",
			withTimezone: true,
		}),
		reviewStatus: underwritingDecisionReviewStatus("review_status").default("pending").notNull(),
		deferredAt: timestamp("deferred_at", {
			mode: "date",
			withTimezone: true,
		}),
		supersededAt: timestamp("superseded_at", {
			mode: "date",
			withTimezone: true,
		}),
		followUps: jsonb("follow_ups").$type<DecisionFollowUp[]>().default(sql`'[]'::jsonb`).notNull(),
		nextFollowUpOn: date("next_follow_up_on", { mode: "string" }),
		followUpClaimedAt: timestamp("follow_up_claimed_at", {
			mode: "date",
			withTimezone: true,
		}),
		followUpAttempts: integer("follow_up_attempts").default(0).notNull(),
		followUpLastError: text("follow_up_last_error"),
		memoryIndexed: boolean("memory_indexed").default(false).notNull(),
		quoteAt: date("quote_at", { mode: "string" }),
		quoteSentAt: timestamp("quote_sent_at", {
			mode: "date",
			withTimezone: true,
		}),
		quoteSendClaimedAt: timestamp("quote_send_claimed_at", {
			mode: "date",
			withTimezone: true,
		}),
		quoteDocumentId: uuid("quote_document_id").references(() => submissionDocuments.id, {
			onDelete: "set null",
		}),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("underwriting_decisions_submission_id_idx").on(table.submissionId),
		index("underwriting_decisions_reviewer_id_idx").on(table.reviewerId),
		uniqueIndex("underwriting_decisions_quote_id_unique")
			.on(table.quoteId)
			.where(sql`${table.quoteId} is not null and ${table.supersededAt} is null`),
		index("underwriting_decisions_quote_send_idx")
			.on(table.quoteAt)
			.where(sql`${table.quoteSentAt} is null`),
		index("underwriting_decisions_follow_up_idx")
			.on(table.nextFollowUpOn)
			.where(sql`${table.nextFollowUpOn} is not null`),
	],
);

export const submissionDecisionJobs = pgTable(
	"submission_decision_jobs",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		submissionId: uuid("submission_id")
			.references(() => submissions.id, { onDelete: "cascade" })
			.notNull(),
		quoteId: uuid("quote_id")
			.references(() => quotes.id, { onDelete: "cascade" })
			.notNull(),
		dueAt: timestamp("due_at", {
			mode: "date",
			withTimezone: true,
		}).notNull(),
		claimedAt: timestamp("claimed_at", {
			mode: "date",
			withTimezone: true,
		}),
		completedAt: timestamp("completed_at", {
			mode: "date",
			withTimezone: true,
		}),
		skippedReason: text("skipped_reason"),
		attempts: integer("attempts").default(0).notNull(),
		lastError: text("last_error"),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("submission_decision_jobs_submission_id_idx").on(table.submissionId),
		unique("submission_decision_jobs_quote_id_unique").on(table.quoteId),
		index("submission_decision_jobs_due_at_idx")
			.on(table.dueAt)
			.where(sql`${table.completedAt} is null`),
	],
);

export type SubmissionDecisionJobSchema = typeof submissionDecisionJobs.$inferSelect;

export const fourEyesReviewStatus = pgEnum("four_eyes_review_status", [
	"requested",
	"reviewed",
	"cancelled",
	"dismissed",
]);
export type FourEyesReviewStatus = (typeof fourEyesReviewStatus.enumValues)[number];

export const fourEyesResolvedReason = pgEnum("four_eyes_resolved_reason", [
	"manual",
	"reassigned",
	"submission_closed",
]);
export type FourEyesResolvedReason = (typeof fourEyesResolvedReason.enumValues)[number];

export const fourEyesReviews = pgTable(
	"four_eyes_reviews",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		submissionId: uuid("submission_id")
			.references(() => submissions.id, { onDelete: "cascade" })
			.notNull(),
		requestedBy: uuid("requested_by").references(() => users.id).notNull(),
		reviewerId: uuid("reviewer_id").references(() => users.id).notNull(),
		requestNote: text("request_note"),
		feedback: jsonb("feedback").$type<FourEyesFeedback>(),
		status: fourEyesReviewStatus("status").default("requested").notNull(),
		reviewedAt: timestamp("reviewed_at", {
			mode: "date",
			withTimezone: true,
		}),
		resolvedAt: timestamp("resolved_at", {
			mode: "date",
			withTimezone: true,
		}),
		resolvedReason: fourEyesResolvedReason("resolved_reason"),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("four_eyes_reviews_submission_id_idx").on(table.submissionId),
		index("four_eyes_reviews_reviewer_pending_idx")
			.on(table.reviewerId)
			.where(sql`${table.status} = 'requested'`),
		uniqueIndex("four_eyes_reviews_live_reviewer_unique")
			.on(table.submissionId, table.reviewerId)
			.where(sql`${table.status} in ('requested', 'reviewed')`),
	],
);

export type FourEyesReviewSchema = typeof fourEyesReviews.$inferSelect;

export const submissionDecisionJobRelations = relations(
	submissionDecisionJobs,
	({ one }) => ({
		submission: one(submissions, {
			fields: [submissionDecisionJobs.submissionId],
			references: [submissions.id],
		}),
		quote: one(quotes, {
			fields: [submissionDecisionJobs.quoteId],
			references: [quotes.id],
		}),
	}),
);

export const submissionPreps = pgTable(
	"submission_preps",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		submissionId: uuid("submission_id")
			.references(() => submissions.id, { onDelete: "cascade" })
			.notNull(),
		kind: text("kind").$type<PrepKind>().notNull(),
		state: text("state").$type<PrepState>().notNull(),
		reason: text("reason"),
		error: text("error"),
		detail: jsonb("detail").$type<PrepDetail>(),
		settledAt: timestamp("settled_at", {
			mode: "date",
			withTimezone: true,
		}),
		createdAt,
	},
	(table) => [
		unique("submission_preps_submission_id_kind_unique").on(table.submissionId, table.kind),
		index("submission_preps_submission_id_state_idx").on(table.submissionId, table.state),
	],
);

export type SubmissionPrepSchema = typeof submissionPreps.$inferSelect;

export const submissionPrepRelations = relations(submissionPreps, ({ one }) => ({
	submission: one(submissions, {
		fields: [submissionPreps.submissionId],
		references: [submissions.id],
	}),
}));

export const submissionGates = pgTable(
	"submission_gates",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		submissionId: uuid("submission_id")
			.references(() => submissions.id, { onDelete: "cascade" })
			.notNull(),
		source: text("source").$type<GateSource>().notNull(),
		state: text("state").$type<GateState>().notNull(),
		armedAt: timestamp("armed_at", {
			mode: "date",
			withTimezone: true,
		}).notNull(),
		deadlineAt: timestamp("deadline_at", {
			mode: "date",
			withTimezone: true,
		}).notNull(),
		readyAt: timestamp("ready_at", {
			mode: "date",
			withTimezone: true,
		}),
		readyReason: text("ready_reason"),
		firedAt: timestamp("fired_at", {
			mode: "date",
			withTimezone: true,
		}),
		completedAt: timestamp("completed_at", {
			mode: "date",
			withTimezone: true,
		}),
		skippedReason: text("skipped_reason"),
		coverage: jsonb("coverage"),
		createdAt,
		updatedAt,
	},
	(table) => [
		// The open index bounds concurrency; the intake index bounds arming over the
		// submission's whole lifetime, which a redelivered follow-up would otherwise breach.
		uniqueIndex("submission_gates_open_unique")
			.on(table.submissionId)
			.where(sql`${table.state} in ('armed', 'ready')`),
		uniqueIndex("submission_gates_intake_unique")
			.on(table.submissionId)
			.where(sql`${table.source} = 'intake'`),
		index("submission_gates_state_deadline_idx").on(table.state, table.deadlineAt),
	],
);

export type SubmissionGateSchema = typeof submissionGates.$inferSelect;

export const submissionGateRelations = relations(submissionGates, ({ one }) => ({
	submission: one(submissions, {
		fields: [submissionGates.submissionId],
		references: [submissions.id],
	}),
}));

export const pilotCompetencyRatings = pgTable(
	"pilot_competency_ratings",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		submissionId: uuid("submission_id")
			.references(() => submissions.id, { onDelete: "cascade" })
			.notNull(),
		ratings: jsonb("ratings")
			.$type<PilotRatingEntry[]>()
			.notNull()
			.default(sql`'[]'::jsonb`),
		level: integer("level"),
		createdAt,
		updatedAt,
	},
	(table) => [
		unique("pilot_competency_ratings_submission_id_unique").on(table.submissionId),
		index("pilot_competency_ratings_level_idx").on(table.level),
	],
);

export const pilotCompetencyRatingRelations = relations(
	pilotCompetencyRatings,
	({ one }) => ({
		submission: one(submissions, {
			fields: [pilotCompetencyRatings.submissionId],
			references: [submissions.id],
		}),
	}),
);

export const submissionReminders = pgTable(
	"submission_reminders",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		submissionId: uuid("submission_id")
			.references(() => submissions.id, { onDelete: "cascade" })
			.notNull(),
		reminderDate: timestamp("reminder_date", {
			mode: "string",
			withTimezone: true,
		}).notNull(),
		sentAt: timestamp("sent_at", {
			mode: "string",
			withTimezone: true,
		}),
		channels: text("channels").array().default(sql`'{}'::text[]`).notNull(),
		createdAt,
		updatedAt,
	},
);

export const submissionTaskType = pgEnum("submission_task_type", [
	"historic_aircraft",
	"alt_address",
	"insured_user_registration_info",
]);
export type SubmissionTaskType = (typeof submissionTaskType.enumValues)[number];

export const submissionTasks = pgTable("submission_tasks", {
	id: uuid().defaultRandom().primaryKey().notNull(),
	submissionId: uuid("submission_id")
		.references(() => submissions.id, { onDelete: "cascade" })
		.notNull(),
	type: submissionTaskType("type").notNull(),
	assignedToKey: text("assigned_to_key").references(() => identities.key, {
		onDelete: "set null",
	}),
	resolution: text("resolution"),
	resolvedAt: timestamp("resolved_at", {
		mode: "date",
		withTimezone: true,
	}),
	createdAt,
	updatedAt,
}, (table) => [
	index("submission_tasks_assigned_to_key_idx").on(table.assignedToKey),
	index("submission_tasks_created_at_idx").on(table.createdAt.desc()),
]);


export const submissionTaskRelations = relations(
	submissionTasks,
	({ one }) => ({
		submission: one(submissions, {
			fields: [submissionTasks.submissionId],
			references: [submissions.id],
		}),
		assignedTo: one(identities, {
			fields: [submissionTasks.assignedToKey],
			references: [identities.key],
		}),
	}),
);

export const submissionDocumentKind = pgEnum("submission_document_kind", [
	"quote",
]);

export type SubmissionDocumentKind = (typeof submissionDocumentKind.enumValues)[number];

export const submissionDocuments = pgTable(
	"submission_documents",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		submissionId: uuid("submission_id")
			.references(() => submissions.id, { onDelete: "cascade" })
			.notNull(),
		quoteId: uuid("quote_id")
			.references(() => quotes.id, { onDelete: "cascade" })
			.notNull(),
		kind: submissionDocumentKind("kind").notNull(),
		storageKey: text("storage_key").notNull(),
		byteSize: integer("byte_size"),
		sha256: text("sha256"),
		payload: jsonb("payload").$type<QuotePrintPayload>().notNull(),
		payloadVersion: integer("payload_version").notNull(),
		generatedByUserId: uuid("generated_by_user_id").references(() => users.id, {
			onDelete: "set null",
		}),
		sentAt: timestamp("sent_at", {
			mode: "date",
			withTimezone: true,
		}),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("submission_documents_submission_id_idx").on(table.submissionId),
		index("submission_documents_quote_id_idx").on(table.quoteId),
	],
);

export type SubmissionDocumentSchema = typeof submissionDocuments.$inferSelect;

export const submissionDocumentRelations = relations(
	submissionDocuments,
	({ one }) => ({
		submission: one(submissions, {
			fields: [submissionDocuments.submissionId],
			references: [submissions.id],
		}),
		quote: one(quotes, {
			fields: [submissionDocuments.quoteId],
			references: [quotes.id],
		}),
		generatedBy: one(users, {
			fields: [submissionDocuments.generatedByUserId],
			references: [users.id],
		}),
	}),
);


export const submissionReminderRelations = relations(
	submissionReminders,
	({ one }) => ({
		submission: one(submissions, {
			fields: [submissionReminders.submissionId],
			references: [submissions.id],
		}),
	}),
);

export const submissionCommentRelations = relations(
	submissionComments,
	({ one }) => ({
		submission: one(submissions, {
			fields: [submissionComments.submissionId],
			references: [submissions.id],
		}),
		author: one(identities, {
			fields: [submissionComments.author],
			references: [identities.key],
		}),
	}),
);

export const submissionIndicationRelations = relations(
	submissionIndications,
	({ one }) => ({
		submission: one(submissions, {
			fields: [submissionIndications.submissionId],
			references: [submissions.id],
		}),
		createdByUser: one(users, {
			fields: [submissionIndications.createdBy],
			references: [users.id],
		}),
		reviewer: one(users, {
			fields: [submissionIndications.reviewerId],
			references: [users.id],
		}),
	}),
);

export const underwritingDecisionRelations = relations(
	underwritingDecisions,
	({ one }) => ({
		submission: one(submissions, {
			fields: [underwritingDecisions.submissionId],
			references: [submissions.id],
		}),
		reviewer: one(users, {
			fields: [underwritingDecisions.reviewerId],
			references: [users.id],
		}),
		quote: one(quotes, {
			fields: [underwritingDecisions.quoteId],
			references: [quotes.id],
		}),
	}),
);

export const fourEyesReviewRelations = relations(
	fourEyesReviews,
	({ one }) => ({
		submission: one(submissions, {
			fields: [fourEyesReviews.submissionId],
			references: [submissions.id],
		}),
		reviewer: one(users, {
			fields: [fourEyesReviews.reviewerId],
			references: [users.id],
		}),
		requester: one(users, {
			fields: [fourEyesReviews.requestedBy],
			references: [users.id],
		}),
	}),
);

export const submissionActivityRelations = relations(
	submissionActivity,
	({ one }) => ({
		submission: one(submissions, {
			fields: [submissionActivity.submissionId],
			references: [submissions.id],
		}),
		performedBy: one(identities, {
			fields: [submissionActivity.performedBy],
			references: [identities.key],
		}),
	}),
);

/** Source that produced the action (api, worker, cron, frontend). */
export const submissionActionSource = pgEnum("submission_action_source", [
	"api",
	"worker",
	"cron",
	"frontend",
	"agent",
]);
export type SubmissionActionSource = (typeof submissionActionSource.enumValues)[number];

/**
 * One underwriting "step" = a time-based grouping of actions (e.g. one session of work).
 * Created when classify-and-prune runs; AI provides description and whether the step was relevant.
 */
export const submissionUnderwritingSteps = pgTable(
	"submission_underwriting_steps",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		submissionId: uuid("submission_id")
			.references(() => submissions.id, { onDelete: "cascade" })
			.notNull(),
		/** Short AI-generated title for the step */
		title: text("title"),
		/** AI-generated: what was done in this step */
		description: text("description").notNull(),
		/** AI-generated: why this is a distinct step (rationale for the grouping) */
		rationale: text("rationale"),
		/** Whether the AI considered this step relevant to the end-to-end flow */
		relevant: boolean("relevant").notNull(),
		/** Order of this step within the submission (0-based) */
		sequence: integer("sequence").notNull(),
		/** Start of the time window for this step */
		startedAt: timestamp("started_at", {
			mode: "string",
			withTimezone: true,
		}).notNull(),
		/** End of the time window for this step */
		endedAt: timestamp("ended_at", {
			mode: "string",
			withTimezone: true,
		}).notNull(),
		createdAt: timestamp("created_at", {
			mode: "string",
			withTimezone: true,
		})
			.defaultNow()
			.notNull(),
	},
	(table) => [
		index("submission_underwriting_steps_submission_id_idx").on(table.submissionId),
	],
);

export type SubmissionUnderwritingStepSchema = typeof submissionUnderwritingSteps.$inferSelect;
export type SubmissionUnderwritingStepInsert = typeof submissionUnderwritingSteps.$inferInsert;

/**
 * Tracks every action in the submission-to-policy flow (emails, status changes, quote bind,
 * underwriter decisions, frontend views/edits). Used for agent crew training and replication.
 */
export const submissionActions = pgTable(
	"submission_actions",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		submissionId: uuid("submission_id")
			.references(() => submissions.id, { onDelete: "cascade" })
			.notNull(),
		/** When set, this action belongs to this underwriting step (from classify-and-prune) */
		underwritingStepId: uuid("underwriting_step_id").references(
			() => submissionUnderwritingSteps.id,
			{ onDelete: "set null" },
		),
		quoteId: uuid("quote_id").references(() => quotes.id, { onDelete: "set null" }),
		policyId: text("policy_id").references(() => policies.id, { onDelete: "set null" }),
		/** Where the action originated: api | worker | cron | frontend */
		source: submissionActionSource("source").notNull(),
		/** Action kind, e.g. email_received, status_updated, quote_created, quote_bound, document_viewed, quote_field_edited */
		actionKind: text("action_kind").notNull(),
		/** Identity key of user who performed (null for system/worker/cron) */
		performedBy: text("performed_by").references(() => identities.key, {
			onDelete: "set null",
		}),
		/** Action-specific payload */
		payload: jsonb("payload")
			.$type<Record<string, unknown>>()
			.notNull()
			.default(sql`'{}'::jsonb`),
		/** AI flow category: input, exploration, action, reaction */
		impactCategory: text("impact_category"),
		/** When assigned to an underwriting step: whether this action was relevant to the other actions in that step (from AI). */
		stepRelevant: boolean("step_relevant"),
		/** When set, this action was reviewed by the action-cleanup cron (e.g. url visit kept as relevant). */
		reviewedAt: timestamp("reviewed_at", {
			mode: "string",
			withTimezone: true,
		}),
		/** When set, AI was unsure; cron will re-review on next run with follow-up actions. */
		markedForDeletionAt: timestamp("marked_for_deletion_at", {
			mode: "string",
			withTimezone: true,
		}),
		/** Soft delete for viewing actions (dedupe); external_url_visited not relevant is soft-deleted. */
		deleted: boolean("deleted").default(false).notNull(),
		/** When marked for deletion or deleted: brief rationale from AI or system (e.g. "developers working in system not real underwriters"). */
		deletionRationale: text("deletion_rationale"),
		createdAt: timestamp("created_at", {
			mode: "string",
			withTimezone: true,
		})
			.defaultNow()
			.notNull(),
	},
	(table) => [
		index("submission_actions_submission_id_idx").on(table.submissionId),
		index("submission_actions_underwriting_step_id_idx").on(table.underwritingStepId),
		index("submission_actions_quote_id_idx").on(table.quoteId),
		index("submission_actions_created_at_idx").on(table.createdAt.desc()),
		index("submission_actions_action_kind_idx").on(table.actionKind),
		index("submission_actions_source_idx").on(table.source),
		index("submission_actions_performed_by_created_at_idx").on(
			table.performedBy,
			table.createdAt,
		),
	],
);

export type SubmissionActionSchema = typeof submissionActions.$inferSelect;
export type SubmissionActionInsert = typeof submissionActions.$inferInsert;

export const submissionUnderwritingStepRelations = relations(
	submissionUnderwritingSteps,
	({ one, many }) => ({
		submission: one(submissions, {
			fields: [submissionUnderwritingSteps.submissionId],
			references: [submissions.id],
		}),
		actions: many(submissionActions),
	}),
);

export const submissionActionRelations = relations(
	submissionActions,
	({ one }) => ({
		submission: one(submissions, {
			fields: [submissionActions.submissionId],
			references: [submissions.id],
		}),
		underwritingStep: one(submissionUnderwritingSteps, {
			fields: [submissionActions.underwritingStepId],
			references: [submissionUnderwritingSteps.id],
		}),
		quote: one(quotes, {
			fields: [submissionActions.quoteId],
			references: [quotes.id],
		}),
		policy: one(policies, {
			fields: [submissionActions.policyId],
			references: [policies.id],
		}),
		performedBy: one(identities, {
			fields: [submissionActions.performedBy],
			references: [identities.key],
		}),
	}),
);

/**
 * Weekly review of underwriter activity: metrics, summary, priorities, and trend insights.
 * Generated by submission-action-weekly-review cron (Monday 7AM EST).
 */
export const underwriterWeeklyReviews = pgTable(
	"underwriter_weekly_reviews",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		/** Identity key of the underwriter */
		performedBy: text("performed_by")
			.references(() => identities.key, { onDelete: "cascade" })
			.notNull(),
		/** Start of the week (Monday) in UTC date */
		weekStart: date("week_start", { mode: "string" }).notNull(),
		/** Aggregate metrics: submissions worked, quoted, bound, action counts by kind, impact category weights */
		metrics: jsonb("metrics")
			.$type<Record<string, unknown>>()
			.notNull()
			.default(sql`'{}'::jsonb`),
		/** AI-generated summary of what the past week looked like */
		summaryStatement: text("summary_statement"),
		/** AI-generated statement of inferred priorities */
		prioritiesStatement: text("priorities_statement"),
		/** AI-generated trend insights comparing to last 2 weekly reviews and previous advice */
		trendInsights: text("trend_insights"),
		/** AI-generated recommendations: where to focus vs other underwriters; for Slack and DB */
		recommendations: text("recommendations"),
		/** Markdown formatted for Slack: summary, priorities, trends, recommendations; emojis and structure */
		slackMessageMarkdown: text("slack_message_markdown"),
		createdAt: timestamp("created_at", {
			mode: "string",
			withTimezone: true,
		})
			.defaultNow()
			.notNull(),
	},
	(table) => [
		index("underwriter_weekly_reviews_performed_by_week_start_idx").on(
			table.performedBy,
			table.weekStart,
		),
		unique("underwriter_weekly_reviews_performed_by_week_start_unique").on(
			table.performedBy,
			table.weekStart,
		),
	],
);

export type UnderwriterWeeklyReviewSchema = typeof underwriterWeeklyReviews.$inferSelect;
export type UnderwriterWeeklyReviewInsert = typeof underwriterWeeklyReviews.$inferInsert;

/**
 * Automation feature recommendations per week: easily automatable vs gen AI candidates.
 * Generated by submission-action-weekly-review cron (Monday 7AM EST).
 */
export const automationFeatureRecommendations = pgTable(
	"automation_feature_recommendations",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		/** Start of the week (Monday) in UTC date */
		weekStart: date("week_start", { mode: "string" }).notNull(),
		/** easily_automatable | gen_ai_candidate */
		category: text("category").notNull(),
		/** List of recommendations with rationale (e.g. [{ feature, rationale, actionKinds, weight }]) */
		recommendations: jsonb("recommendations")
			.$type<Record<string, unknown>[]>()
			.notNull()
			.default(sql`'[]'::jsonb`),
		createdAt: timestamp("created_at", {
			mode: "string",
			withTimezone: true,
		})
			.defaultNow()
			.notNull(),
	},
	(table) => [
		index("automation_feature_recommendations_week_start_idx").on(table.weekStart),
		unique("automation_feature_recommendations_week_start_category_unique").on(
			table.weekStart,
			table.category,
		),
	],
);

export type AutomationFeatureRecommendationSchema =
	typeof automationFeatureRecommendations.$inferSelect;
export type AutomationFeatureRecommendationInsert =
	typeof automationFeatureRecommendations.$inferInsert;

/**
 * Weekly Slack message for automation feature recommendations (one per week).
 * Generated by submission-action-weekly-review cron; can be sent to Slack.
 */
export const automationWeeklySlackMessages = pgTable(
	"automation_weekly_slack_messages",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		/** Start of the week (Monday) in UTC date */
		weekStart: date("week_start", { mode: "string" }).notNull().unique(),
		/** Markdown formatted for Slack: easily automatable + gen AI candidates; emojis and structure */
		slackMessageMarkdown: text("slack_message_markdown").notNull(),
		createdAt: timestamp("created_at", {
			mode: "string",
			withTimezone: true,
		})
			.defaultNow()
			.notNull(),
	},
	(table) => [index("automation_weekly_slack_messages_week_start_idx").on(table.weekStart)],
);

export type AutomationWeeklySlackMessageSchema =
	typeof automationWeeklySlackMessages.$inferSelect;
export type AutomationWeeklySlackMessageInsert =
	typeof automationWeeklySlackMessages.$inferInsert;

/**
 * Weekly Slack message comparing all underwriters (one per week).
 * Generated by submission-action-weekly-review cron after individual underwriter reviews.
 * For the head of underwriting: strengths/weaknesses and mentoring focus.
 */
export const underwriterComparisonSlackMessages = pgTable(
	"underwriter_comparison_slack_messages",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		/** Start of the week (Monday) in UTC date */
		weekStart: date("week_start", { mode: "string" }).notNull().unique(),
		/** Markdown formatted for Slack: comparison, strengths/weaknesses per underwriter; for mentoring */
		slackMessageMarkdown: text("slack_message_markdown").notNull(),
		createdAt: timestamp("created_at", {
			mode: "string",
			withTimezone: true,
		})
			.defaultNow()
			.notNull(),
	},
	(table) => [index("underwriter_comparison_slack_messages_week_start_idx").on(table.weekStart)],
);

export type UnderwriterComparisonSlackMessageSchema =
	typeof underwriterComparisonSlackMessages.$inferSelect;
export type UnderwriterComparisonSlackMessageInsert =
	typeof underwriterComparisonSlackMessages.$inferInsert;

export const submissionsAircraftRelations = relations(
	submissionsAircraft,
	({ one }) => ({
		aircraft: one(aircraft, {
			fields: [submissionsAircraft.aircraftId],
			references: [aircraft.id],
		}),
		submission: one(submissions, {
			fields: [submissionsAircraft.submissionId],
			references: [submissions.id],
		}),
	}),
);

export type HydratedSubmission = Omit<
	SubmissionSchema,
  "assignedTo" | "broker"
> & {
	assignedTo:
    | null
    | (Pick<UserIdentitySchema, "email" | "id"> & {
    	identity: typeof identities.$inferSelect;
    });
} & {
	broker: null | {
		identity: typeof identities.$inferSelect;
		brokerage: typeof brokerages.$inferSelect;
	};
} & {
	quotes: Array<{ id: string }>;
	nextReminderDate: string | null;
};

export type SubmissionInsertSchema = typeof submissions.$inferInsert;

export type SubmissionSchema = typeof submissions.$inferSelect;

/** Reinsurance line position on the quote and copied to policy at bind. */
export const lineRole = pgEnum("line_role", ["lead", "follow"]);
export type LineRole = (typeof lineRole.enumValues)[number];

export const quotes = pgTable("quotes", {
	id: uuid().defaultRandom().primaryKey().notNull(),
	createdAt,
	updatedAt,
	insured: jsonb("insured").$type<Party>(),
	producer: jsonb("producer").$type<Party>(),
	submissionId: uuid("submission_id")
		.references(() => submissions.id, { onDelete: "cascade" })
		.notNull(),
	aircraft: jsonb("aircraft")
		.$type<QuoteAircraft[]>()
		.notNull()
		.default(sql`'[]'::jsonb`),
	costs: jsonb("costs")
		.$type<CostLineItem[]>()
		.notNull()
		.default(sql`'[]'::jsonb`),
	endorsementsLegacy: jsonb("endorsements_legacy")
		.$type<Endorsement[]>()
		.notNull()
		.default(sql`'[]'::jsonb`),
	inceptionDate: date("inception_date", {
		mode: "string",
	}).notNull(),
	expiryDate: date("expiry_date", {
		mode: "string",
	}).notNull(),
	brokerCommission: doublePrecision("broker_commission").notNull(),
	omitBrokerCommission: boolean("omit_broker_commission").default(false).notNull(),
	pilotWarrantyMarkdown: text("pilot_warranty_markdown"),
	lineSize: doublePrecision("line_size").default(100).notNull(),
	lineLead: text("line_lead").default("Class A").notNull(),
	lineFollow: text("line_follow"),
	lineRole: lineRole("line_role").default("lead").notNull(),
	/** Lead carrier policy number when this quote is follow on a quota share; copied to policy at bind. */
	leadPolicyNumber: text("lead_policy_number"),
	underwriterComments: text("underwriter_comments"),
	alternateQuotes: jsonb("alternate_quotes")
		.$type<AlternateQuote[]>()
		.notNull()
		.default(sql`'[]'::jsonb`),
	lobData: jsonb("lob_data").$type<QuoteLobData | null>().default(null),
	templateSlug: text("template_slug"),
});

export const quoteEndorsements = pgTable(
	"quote_endorsements",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		quoteId: uuid("quote_id")
			.references(() => quotes.id, { onDelete: "cascade" })
			.notNull(),
		slug: text("slug").notNull(),
		data: jsonb("data").$type<typeof GenericEndorsementSchema.statics>(),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("quote_endorsements_quote_id_idx").on(table.quoteId),
		// Note: unique constraint is manually managed via custom migration (partial unique on quote_id+slug). Multiple rows per slug allowed for: custom_endorsement, loss_control_safety, crew_training, additional_insured, additional_insured_pilot_service_provider, lienholders_interest, loss_payable, constructive_total_loss, floats_and_skis, financed_premium, war_hijacking_perils, liability, policy_territory, owner_lessor_endorsement, amendatory, leased_engine_scheduled, physical_damage, uas_liability_amendment, uas_physical_damage, uas_not_in_motion_physical_damage, uas_war_liability_write_back, uas_additional_insured, uas_ag_spray_chemical_liability, pl_additional_insured, layup_return, increased_limits_designated_persons, waiver_of_subrogation, waiver_of_subrogation_av, contractual_liability_specific_contract, lienholders_interest_multiple, layup_return_single, special_equipment, managed_aircraft, managed_aircraft_broad, reporting_form_premium, premium_audit_adjustment, schedule_of_contracts_av, finance_lease_contract, aircraft_finance_lease_contract, finance_lease_contract_liability, additional_insured_extended_schedule, honeywell_leased_engine, additional_insured_workmanship_exception, hangarkeepers_waiver, uas_addition_change, disassembled_aircraft_physical_damage, ground_storage_limitation, fractional_ownership_excess_liability, layup_return_fleet_registration.
	],
);
export type QuoteEndorsementSchema = typeof quoteEndorsements.$inferSelect;

export const latestLocations = pgTable(
	"latest_locations",
	{
		aircraftId: uuid("aircraft_id")
			.references(() => aircraft.id, { onDelete: "cascade" })
			.primaryKey()
			.notNull(),
		timestamp: timestamp("timestamp", {
			mode: "date",
			withTimezone: true,
		}).notNull(),
		location: geometry("location", { mode: "tuple", type: "point" }).notNull(),
		altitude: doublePrecision("altitude"),
		speed: doublePrecision("speed"),
		heading: doublePrecision("heading"),
	},
	(table) => [
		index("latest_locations_spacial_idx").using("gist", table.location),
		index("latest_locations_timestamp_idx").on(table.timestamp),
	],
);

export type LatestLocationSchema = typeof latestLocations.$inferSelect;

export const policyStatus = pgEnum("policy_status", [
	"active",
	"expired",
	"cancelled",
]);
export type PolicyStatus = (typeof policyStatus.enumValues)[number];

export const policies = pgTable("policies", {
	id: text("id").primaryKey().notNull(),
	accountId: uuid("account_id").references(() => accounts.id, {
		onDelete: "set null",
	}),
	namedInsured: text("named_insured"),
	attention: text("attention"),
	streetAddress: text("street_address"),
	city: text("city"),
	state: text("state"),
	postalCode: text("postal_code"),
	country: text("country"),
	startDate: date("start_date", {
		mode: "string",
	}).notNull(),
	endDate: date("end_date", {
		mode: "string",
	}).notNull(),
	bookedAt: timestamp("booked_at", {
		mode: "string",
		withTimezone: true,
	}),
	/** Earliest `policy_endorsements.created_at` with non-null `effective_date` and a non-zero premium signal in `data` (see `policyEndorsementDataHasQualifyingMidTermPremium`). Used to lock inception invoice premium edits in amend mode. */
	midTermAppliedAt: timestamp("mid_term_applied_at", {
		mode: "string",
		withTimezone: true,
	}),
	status: policyStatus("status").default("active").notNull(),
	lob: linesOfBusiness("lob").default("general_aviation").notNull(),
	quoteId: uuid("quote_id")
		.references(() => quotes.id, { onDelete: "set null" }),
	previousPolicyId: text("previous_policy_id")
		.references((): AnyPgColumn => policies.id, { onDelete: "set null" }),
	underwriterId: uuid("underwriter_id")
		.references(() => users.id, { onDelete: "set null" }),
	brokerEmail: text("broker_email").references(() => brokers.email, { onDelete: "set null" }),
	gloveboxAutoBrokerReviewAfter30d: boolean("glovebox_auto_broker_review_after_30d")
		.default(true)
		.notNull(),
	gloveboxAutoPublishCertsAfterBind: boolean("glovebox_auto_publish_certs_after_bind")
		.default(true)
		.notNull(),
	/** Snapshot from quote at bind; editable until booked. Null on legacy rows — fallback to quote. */
	brokerCommission: doublePrecision("broker_commission"),
	lineSize: doublePrecision("line_size"),
	lineLead: text("line_lead"),
	lineFollow: text("line_follow"),
	lineRole: lineRole("line_role"),
	leadPolicyNumber: text("lead_policy_number"),
	/** Bind-time fee snapshot only (Mexican / state / policy fee). Aircraft & endorsement premiums stay on the quote. */
	costs: jsonb("costs")
		.$type<CostLineItem[]>()
		.notNull()
		.default(sql`'[]'::jsonb`),
	metadata: jsonb("metadata")
		.$type<PolicyMetadata>()
		.notNull()
		.default(sql`'{}'::jsonb`),
	createdAt,
	updatedAt,
}, (table) => [
	unique("policies_quote_id_unique_non_null").on(table.quoteId).nullsNotDistinct(),
	index("policies_previous_policy_id_idx").on(table.previousPolicyId),
	index("policies_status_end_date_idx").on(table.status, table.endDate),
]);

export const policyEndorsements = pgTable(
	"policy_endorsements",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		policyId: text("policy_id")
			.references(() => policies.id, { onDelete: "cascade" })
			.notNull(),
		quoteEndorsementId: uuid("quote_endorsement_id")
			.references(() => quoteEndorsements.id, { onDelete: "set null" }),
		slug: text("slug").notNull(),
		data: jsonb("data").$type<typeof GenericEndorsementSchema.statics>(),
		invoiceId: uuid("invoice_id")
			.references(() => invoices.id, { onDelete: "set null" }),
		aircraftId: uuid("aircraft_id")
			.references(() => aircraft.id, { onDelete: "set null" }),
		effectiveDate: date("effective_date", { mode: "string" }),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("policy_endorsements_policy_id_idx").on(table.policyId),
	],
);

export type PolicyEndorsementSchema = typeof policyEndorsements.$inferSelect;

export const policyEndorsementRelations = relations(policyEndorsements, ({ one }) => ({
	policy: one(policies, {
		fields: [policyEndorsements.policyId],
		references: [policies.id],
	}),
	quoteEndorsement: one(quoteEndorsements, {
		fields: [policyEndorsements.quoteEndorsementId],
		references: [quoteEndorsements.id],
	}),
	invoice: one(invoices, {
		fields: [policyEndorsements.invoiceId],
		references: [invoices.id],
	}),
	aircraft: one(aircraft, {
		fields: [policyEndorsements.aircraftId],
		references: [aircraft.id],
	}),
}));

export const usersPolicies = pgTable(
	"users_policies",
	{
		policyId: text("policy_id")
			.references(() => policies.id, { onDelete: "cascade" })
			.notNull(),
		userId: uuid("user_id")
			.references(() => users.id, { onDelete: "cascade" })
			.notNull(),
		asRole: text("as_role").default("insured").notNull(),
		createdAt,
		updatedAt,
	},
	(table) => [
		primaryKey({ columns: [table.userId, table.policyId] }),
		index("users_policies_policy_id_idx").on(table.policyId),
		index("users_policies_user_id_idx").on(table.userId),
	],
);

export const deductibleType = pgEnum("deductible_type", [
	"fixed_amount",
	"percentage",
]);
export const policiesAircraft = pgTable(
	"policies_aircraft",
	{
		policyId: text("policy_id")
			.references(() => policies.id, { onDelete: "cascade" })
			.notNull(),
		aircraftId: uuid("aircraft_id")
			.references(() => aircraft.id, { onDelete: "cascade" })
			.notNull(),
		defaultPilot: uuid("default_pilot").references(() => pilots.id, {
			onDelete: "set null",
		}),
		insuredValue: doublePrecision("insured_value"),
		hullRate: doublePrecision("hull_rate"),
		hullPremium: doublePrecision("hull_premium").generatedAlwaysAs(
			sql`CASE
				WHEN hull_rate IS NULL OR insured_value IS NULL THEN NULL
				ELSE (hull_rate / 100.0) * insured_value
			END`,
		),
		coverageLetter: text("coverage_letter"),
		liabilityLimit: doublePrecision("liability_limit"),
		perPassengerLiabilitySublimit: doublePrecision("per_passenger_liability_sublimit"),
		medicalPayments: doublePrecision("medical_payments"),
		imDeductible: doublePrecision("im_deductible"),
		nimDeductible: doublePrecision("nim_deductible"),
		deductibleType: deductibleType("deductible_type").default("fixed_amount"),
		liabilityPremium: doublePrecision("liability_premium"),
		hullWarRate: doublePrecision("hull_war_rate"),
		hullWarPremium: doublePrecision("hull_war_premium").generatedAlwaysAs(
			sql`CASE
				WHEN hull_war_rate IS NULL OR insured_value IS NULL THEN NULL
				ELSE (hull_war_rate / 100.0) * insured_value
			END`,
		),
		warLiabilityPremium: doublePrecision("war_liability_premium"),
		triaLiabilityPremium: doublePrecision("tria_liability_premium"),
		useType: text("use_type"),
		subUse: text("sub_use"),
		// TRIA premium is tracked only on policy endorsements (tria_liability / tria_hull_write_back), not here.
		totalPremium: doublePrecision("total_premium").generatedAlwaysAs(
			sql`COALESCE(
				CASE
					WHEN hull_rate IS NULL OR insured_value IS NULL THEN NULL
					ELSE (hull_rate / 100.0) * insured_value
				END,
				0
			) + COALESCE(liability_premium, 0) + COALESCE(
				CASE
					WHEN hull_war_rate IS NULL OR insured_value IS NULL THEN NULL
					ELSE (hull_war_rate / 100.0) * insured_value
				END,
				0
			) + COALESCE(war_liability_premium, 0)`,
		),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("policies_aircraft_aircraft_id_idx").on(table.aircraftId),
		primaryKey({ columns: [table.policyId, table.aircraftId] }),
		index("policies_aircraft_policy_id_idx").on(table.policyId),
	],
);

export const policiesUas = pgTable(
	"policies_uas",
	{
		policyId: text("policy_id")
			.references(() => policies.id, { onDelete: "cascade" })
			.notNull(),
		uasId: uuid("uas_id")
			.references(() => uas.id, { onDelete: "cascade" })
			.notNull(),
		insuredValue: doublePrecision("insured_value"),
		hullRate: doublePrecision("hull_rate"),
		hullPremium: doublePrecision("hull_premium").generatedAlwaysAs(
			sql`CASE
				WHEN hull_rate IS NULL OR insured_value IS NULL THEN NULL
				ELSE (hull_rate / 100.0) * insured_value
			END`,
		),
		deductible: doublePrecision("deductible"),
		deductibleType: deductibleType("deductible_type").notNull().default("fixed_amount"),
		liabilityLimit: doublePrecision("liability_limit"),
		liabilityPremium: doublePrecision("liability_premium"),
		hullWarRate: doublePrecision("hull_war_rate"),
		hullWarPremium: doublePrecision("hull_war_premium").generatedAlwaysAs(
			sql`CASE
				WHEN hull_war_rate IS NULL OR insured_value IS NULL THEN NULL
				ELSE (hull_war_rate / 100.0) * insured_value
			END`,
		),
		warLiabilityPremium: doublePrecision("war_liability_premium"),
		agEnabled: boolean("ag_enabled").notNull().default(false),
		chemicalEachPersonLimit: doublePrecision("chemical_each_person_limit"),
		chemicalEachOccurrenceLimit: doublePrecision("chemical_each_occurrence_limit"),
		chemicalAggregateLimit: doublePrecision("chemical_aggregate_limit"),
		chemicalPremium: doublePrecision("chemical_premium"),
		totalPremium: doublePrecision("total_premium").generatedAlwaysAs(
			sql`COALESCE(
				CASE
					WHEN hull_rate IS NULL OR insured_value IS NULL THEN NULL
					ELSE (hull_rate / 100.0) * insured_value
				END,
				0
			) + COALESCE(liability_premium, 0) + COALESCE(
				CASE
					WHEN hull_war_rate IS NULL OR insured_value IS NULL THEN NULL
					ELSE (hull_war_rate / 100.0) * insured_value
				END,
				0
			) + COALESCE(war_liability_premium, 0)`,
		),
		createdAt,
		updatedAt,
	},
	(table) => [
		primaryKey({ columns: [table.policyId, table.uasId] }),
		index("policies_uas_uas_id_idx").on(table.uasId),
	],
);

export const policiesPilots = pgTable(
	"policies_pilots",
	{
		policyId: text("policy_id")
			.references(() => policies.id, { onDelete: "cascade" })
			.notNull(),
		pilotId: uuid("pilot_id")
			.references(() => pilots.id, { onDelete: "cascade" })
			.notNull(),
		isDefault: boolean("is_default").default(false).notNull(),
	},
	(table) => [
		primaryKey({ columns: [table.policyId, table.pilotId] }),
		index("policies_pilots_policy_id_idx").on(table.policyId),
		index("policies_pilots_pilot_id_idx").on(table.pilotId),
	],
);
export const policyCertType = pgEnum("policy_cert_type", [
	"to_whom",
	"crew_training",
	"lienholder",
	"additional_insured",
	"charter_air_taxi",
	"eu_785_2004",
	"leased_engine",
	"pilot_service_provider",
]);
export const policyCerts = pgTable(
	"policy_certs",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		policyId: text("policy_id")
			.references(() => policies.id, { onDelete: "cascade" })
			.notNull(),
		certType: policyCertType("cert_type").notNull(),
		certNumber: integer("cert_number").notNull(),
		title: text("title"),
		quoteEndorsementId: uuid("quote_endorsement_id").references(
			() => quoteEndorsements.id,
		),
		policyEndorsementId: uuid("policy_endorsement_id").references(
			() => policyEndorsements.id,
			{ onDelete: "set null" },
		),
		schedule: text("schedule").array(),
		issuedDate: date("issued_date", { mode: "string" }).notNull(),
		createdAt,
		updatedAt,
	},
);

export const policyGloveboxDocumentKind = pgEnum("policy_glovebox_document_kind", [
	"policy_certificate",
	"policy_form",
	"file",
]);

export const policyGloveboxDocuments = pgTable(
	"policy_glovebox_documents",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		policyId: text("policy_id")
			.references(() => policies.id, { onDelete: "cascade" })
			.notNull(),
		kind: policyGloveboxDocumentKind("kind").notNull(),
		/** `policy_form`, `cert:{uuid}`, or `file:{n}` — unique per policy with `policy_id` */
		slotKey: text("slot_key").notNull(),
		/** Sequential index per policy for `kind === "file"` (1, 2, …). */
		fileNumber: integer("file_number"),
		/** Original client filename for manually uploaded glovebox files (`kind === "file"`). */
		uploadedFileName: text("uploaded_file_name"),
		policyCertId: uuid("policy_cert_id").references(() => policyCerts.id, {
			onDelete: "cascade",
		}),
		storageKey: text("storage_key").notNull(),
		contentReplacedAt: timestamp("content_replaced_at", {
			mode: "date",
			withTimezone: true,
		}).notNull(),
		brokerReviewedAt: timestamp("broker_reviewed_at", {
			mode: "date",
			withTimezone: true,
		}),
		brokerReviewedByUserId: uuid("broker_reviewed_by_user_id").references(
			() => users.id,
			{ onDelete: "set null" },
		),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("policy_glovebox_documents_policy_id_idx").on(table.policyId),
		unique("policy_glovebox_documents_policy_slot_unique").on(
			table.policyId,
			table.slotKey,
		),
	],
);

export const policyGloveboxDocumentRelations = relations(
	policyGloveboxDocuments,
	({ one }) => ({
		policy: one(policies, {
			fields: [policyGloveboxDocuments.policyId],
			references: [policies.id],
		}),
		policyCert: one(policyCerts, {
			fields: [policyGloveboxDocuments.policyCertId],
			references: [policyCerts.id],
		}),
		brokerReviewedBy: one(users, {
			fields: [policyGloveboxDocuments.brokerReviewedByUserId],
			references: [users.id],
		}),
	}),
);

export const policyCertRelations = relations(policyCerts, ({ one }) => ({
	policy: one(policies, {
		fields: [policyCerts.policyId],
		references: [policies.id],
	}),
	policyEndorsement: one(policyEndorsements, {
		fields: [policyCerts.policyEndorsementId],
		references: [policyEndorsements.id],
	}),
	quoteEndorsement: one(quoteEndorsements, {
		fields: [policyCerts.quoteEndorsementId],
		references: [quoteEndorsements.id],
	}),
	gloveboxDocument: one(policyGloveboxDocuments, {
		fields: [policyCerts.id],
		references: [policyGloveboxDocuments.policyCertId],
	}),
}));

/** Groups inception invoices split into N installments at booking (`segment_count` = N). */
export const invoicePaymentTerms = pgTable("invoice_payment_terms", {
	id: uuid().defaultRandom().primaryKey().notNull(),
	segmentCount: integer("segment_count").notNull(),
	/** First / last calendar due anchors for installments (remaining dues spread evenly). Nullable on legacy rows. */
	firstDueDate: date("first_due_date", { mode: "string" }),
	lastDueDate: date("last_due_date", { mode: "string" }),
	createdAt,
	updatedAt,
});

export const qbInvoiceType = pgEnum("qb_invoice_type", [
	"invoice",
	"credit_memo",
]);

/** QuickBooks Payment / CreditMemo rows applied to an invoice, mirrored from QBO for billing locks and history. */
export type InvoiceQbAppliedTxnSnapshot = {
	kind: "payment" | "credit_memo";
	qbId: string;
	txnDate: string | null;
	appliedAmount: number | null;
};

export const invoices = pgTable(
	"invoices",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		invoiceNumber: serial("invoice_number").notNull().unique(),
		policyId: text("policy_id")
			.references(() => policies.id, { onDelete: "set null" }),
		brokerageId: uuid("brokerage_id")
			.references(() => brokerages.id, { onDelete: "set null" }),
		qbInvoiceType: qbInvoiceType("qb_invoice_type").default("invoice").notNull(),
		externalId: text("external_id").notNull(),
		invoiceDate: timestamp("invoice_date", { mode: "string", withTimezone: true }).notNull(),
		dueDate: timestamp("due_date", { mode: "string", withTimezone: true }).notNull(),
		payments: jsonb("payments")
			.$type<InvoiceQbAppliedTxnSnapshot[]>()
			.notNull()
			.default(sql`'[]'::jsonb`),
		fullyPaidDate: timestamp("fully_paid_date", { mode: "string", withTimezone: true }),
		invoicePaymentTermsId: uuid("invoice_payment_terms_id").references(() => invoicePaymentTerms.id, {
			onDelete: "set null",
		}),
		createdAt,
		updatedAt,
	},
);

export const invoicePaymentTermsRelations = relations(invoicePaymentTerms, ({ many }) => ({
	invoices: many(invoices),
}));

export const invoiceLineType = pgEnum("invoice_line_type", [
	"policy_fee",
	"inception_premium",
	"mexican_certificate",
	"state_fee",
	"broker_commission",
	"endorsement",
	"policy_cancellation",
]);

export const invoiceLines = pgTable(
	"invoice_lines",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		invoiceId: uuid("invoice_id")
			.references(() => invoices.id, { onDelete: "cascade" })
			.notNull(),
		type: invoiceLineType("type").notNull(),
		amount: doublePrecision("amount").notNull(),
	});
    
export const invoiceRelations = relations(invoices, ({ many, one }) => ({
	policy: one(policies, {
		fields: [invoices.policyId],
		references: [policies.id],
	}),
	brokerage: one(brokerages, {
		fields: [invoices.brokerageId],
		references: [brokerages.id],
	}),
	invoicePaymentTerms: one(invoicePaymentTerms, {
		fields: [invoices.invoicePaymentTermsId],
		references: [invoicePaymentTerms.id],
	}),
	lines: many(invoiceLines),
	policyEndorsements: many(policyEndorsements),
}));

export const invoiceLineRelations = relations(invoiceLines, ({ one }) => ({
	invoice: one(invoices, {
		fields: [invoiceLines.invoiceId],
		references: [invoices.id],
	}),
}));

export const claims = pgTable(
	"claims",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		claimNumber: text("claim_number").notNull().unique(),
		policyId: text("policy_id")
			.references(() => policies.id, { onDelete: "cascade" })
			.notNull(),
		aircraftId: uuid("aircraft_id")
			.references(() => aircraft.id, { onDelete: "cascade" }),
		createdBy: text("created_by").references(() => identities.key, {
			onDelete: "set null",
		}),
		date: timestamp("date", { mode: "date", withTimezone: true }).notNull(),
		description: text("description").notNull(),
		status: text("status").notNull().default("open"),
		address: text("address"),
		location: geometry("location", { mode: "tuple", type: "point" }),
		photos: text("photos")
			.array()
			.default(sql`ARRAY[]::text[]`)
			.$type<string[]>()
			.notNull(),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("claims_created_by_idx").on(table.createdBy),
		index("claims_policy_id_idx").on(table.policyId),
		index("claims_claim_number_idx").on(table.claimNumber),
		index("claims_date_idx").on(table.date.desc()),
		index("claims_status_idx").on(table.status),
	],
);

export const claimsRelations = relations(claims, ({ one }) => ({
	policy: one(policies, {
		fields: [claims.policyId],
		references: [policies.id],
	}),
	createdBy: one(identities, {
		fields: [claims.createdBy],
		references: [identities.key],
	}),
	aircraft: one(aircraft, {
		fields: [claims.aircraftId],
		references: [aircraft.id],
	}),
}));

export const policyRelations = relations(policies, ({ many, one }) => ({
	account: one(accounts, {
		fields: [policies.accountId],
		references: [accounts.id],
	}),
	usersPolicies: many(usersPolicies),
	aircraft: many(policiesAircraft),
	uas: many(policiesUas),
	pilots: many(policiesPilots),
	claims: many(claims),
	invoices: many(invoices),
	certs: many(policyCerts),
	gloveboxDocuments: many(policyGloveboxDocuments),
	policyEndorsements: many(policyEndorsements),
	directMailLetters: many(directMailLetters),
	quote: one(quotes, {
		fields: [policies.quoteId],
		references: [quotes.id],
	}),
	underwriter: one(users, {
		fields: [policies.underwriterId],
		references: [users.id],
	}),
	broker: one(brokers, {
		fields: [policies.brokerEmail],
		references: [brokers.email],
	}),
	previousPolicy: one(policies, {
		fields: [policies.previousPolicyId],
		references: [policies.id],
		relationName: "policyRenewalLineage",
	}),
	renewalPolicies: many(policies, { relationName: "policyRenewalLineage" }),
}));

export const policiesAircraftRelations = relations(
	policiesAircraft,
	({ one }) => ({
		policy: one(policies, {
			fields: [policiesAircraft.policyId],
			references: [policies.id],
		}),
		aircraft: one(aircraft, {
			fields: [policiesAircraft.aircraftId],
			references: [aircraft.id],
		}),
		defaultPilot: one(pilots, {
			fields: [policiesAircraft.defaultPilot],
			references: [pilots.id],
		}),
	}),
);

export const policiesUasRelations = relations(
	policiesUas,
	({ one }) => ({
		policy: one(policies, {
			fields: [policiesUas.policyId],
			references: [policies.id],
		}),
		uas: one(uas, {
			fields: [policiesUas.uasId],
			references: [uas.id],
		}),
	}),
);

export const uasRelations = relations(uas, ({ many }) => ({
	policiesUas: many(policiesUas),
}));

export const policiesPilotsRelations = relations(
	policiesPilots,
	({ one }) => ({
		policy: one(policies, {
			fields: [policiesPilots.policyId],
			references: [policies.id],
		}),
		pilot: one(pilots, {
			fields: [policiesPilots.pilotId],
			references: [pilots.id],
		}),
	}),
);

export const pilotRelations = relations(pilots, ({ many, one }) => ({
	identity: one(identities, {
		fields: [pilots.identityKey],
		references: [identities.key],
	}),
	policies: many(policiesPilots),
}));

export const usersPoliciesRelations = relations(usersPolicies, ({ one }) => ({
	policy: one(policies, {
		fields: [usersPolicies.policyId],
		references: [policies.id],
	}),
	user: one(users, {
		fields: [usersPolicies.userId],
		references: [users.id],
	}),
}));

export type HydratedPolicy = Omit<PolicySchema, "bookedAt"> & {
	aircraft: AircraftSchema[];
	users: Array<{
		asRole: null | string;
		email: null | string;
		name: null | string;
	}>;
};

export type PolicyInsertSchema = typeof policies.$inferInsert;
export type PolicySchema = typeof policies.$inferSelect;

export const airports = pgTable(
	"airports",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		locId: text("loc_id"),
		iataId: text("iata_id"),
		icaoId: text("icao_id"),
		city: text("city"),
		elevation: doublePrecision("elevation"),
		facilityType: text("facility_type"),
		location: geometry("location", { mode: "tuple", type: "point" }),
		name: text("name").notNull(),
		state: text("state"),
		country: text("country"),
		tz: text("tz"),
	},
	(table) => [
		index("loc_id_idx").on(table.locId),
		index("icao_id_idx").on(table.icaoId),
		index("iata_id_idx").on(table.iataId),
		unique("unique_airport_code").on(table.icaoId, table.iataId, table.locId),
	],
);

export type AirportDBSchema = typeof airports.$inferSelect;

export const runways = pgTable(
	"runways",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		airportId: uuid("airport_id")
			.references(() => airports.id, { onDelete: "cascade" })
			.notNull(),
		asdaBase: doublePrecision("asda_base"),
		asdaReciprocal: doublePrecision("asda_reciprocal"),
		baseId: text("base_id"),
		displacedThresholdBase: doublePrecision("displaced_threshold_base"),
		displacedThresholdPointBase: geometry("displaced_threshold_point_base", {
			mode: "tuple",
			type: "point",
		}),
		displacedThresholdReciprocal: doublePrecision(
			"displaced_threshold_reciprocal",
		),
		displacedThresholdPointReciprocal: geometry(
			"displaced_threshold_point_reciprocal",
			{ mode: "tuple", type: "point" },
		),
		edgeLightingIntensity: text("edge_lighting_intensity"),
		endpointBase: geometry("endpoint_base", { mode: "tuple", type: "point" }),
		endpointReciprocal: geometry("endpoint_reciprocal", {
			mode: "tuple",
			type: "point",
		}),
		identifier: text("identifier").notNull(),
		ldaBase: doublePrecision("lda_base"),
		ldaReciprocal: doublePrecision("lda_reciprocal"),
		length: doublePrecision("length").notNull(),
		reciprocalId: text("reciprocal_id"),
		surface: polygon("surface"),
		surfaceCondition: text("surface_condition").notNull(),
		surfaceTreatment: text("surface_treatment").notNull(),
		surfaceType: text("surface_type").notNull(),
		surfaceTypeCondition: text("surface_type_condition"),
		tdzeBase: doublePrecision("tdze_base"),
		tdzeReciprocal: doublePrecision("tdze_reciprocal"),
		todaBase: doublePrecision("toda_base"),
		todaReciprocal: doublePrecision("toda_reciprocal"),
		toraBase: doublePrecision("tora_base"),
		toraReciprocal: doublePrecision("tora_reciprocal"),
		trueHeadingBase: doublePrecision("true_heading_base"),
		trueHeadingReciprocal: doublePrecision("true_heading_reciprocal"),
		width: doublePrecision("width").notNull(),
		vgsiBase: text("vgsi_base"),
		vgsiReciprocal: text("vgsi_reciprocal"),
		visualGlidePathAngleBase: doublePrecision("visual_glide_path_angle_base"),
		visualGlidePathAngleReciprocal: doublePrecision(
			"visual_glide_path_angle_reciprocal",
		),
		visualTchBase: doublePrecision("visual_tch_base"),
		visualTchReciprocal: doublePrecision("visual_tch_reciprocal"),
		ilsGsAngleBase: doublePrecision("ils_gs_angle_base"),
		ilsGsAngleReciprocal: doublePrecision("ils_gs_angle_reciprocal"),
		ilsGsPointBase: geometry("ils_gs_point_base", {
			mode: "tuple",
			type: "point",
		}),
		ilsGsPointReciprocal: geometry("ils_gs_point_reciprocal", {
			mode: "tuple",
			type: "point",
		}),
		ilsGsElevationBase: doublePrecision("ils_gs_elevation_base"),
		ilsGsElevationReciprocal: doublePrecision("ils_gs_elevation_reciprocal"),
		ilsApproachBearingBase: doublePrecision("ils_approach_bearing_base"),
		ilsApproachBearingReciprocal: doublePrecision(
			"ils_approach_bearing_reciprocal",
		),
		ilsTchBase: doublePrecision("ils_tch_base"),
		ilsTchReciprocal: doublePrecision("ils_tch_reciprocal"),
		rnavGpAngleBase: doublePrecision("rnav_gp_angle_base"),
		rnavGpAngleReciprocal: doublePrecision("rnav_gp_angle_reciprocal"),
		rnavTchBase: doublePrecision("rnav_tch_base"),
		rnavTchReciprocal: doublePrecision("rnav_tch_reciprocal"),
		runwayMetadata: jsonb("runway_metadata"),
	},
	(table) => [
		index("airport_id_idx").on(table.airportId),
		index("identifier_idx").on(table.identifier),
		unique("unique_airport_identifier").on(table.airportId, table.identifier),
	],
);
export type RunwayInsertSchema = typeof runways.$inferInsert;
export type RunwaySchema = typeof runways.$inferSelect;

export const airportRelations = relations(airports, ({ many }) => ({
	runways: many(runways),
	airportStations: many(airportStations),
}));

export const runwayRelations = relations(runways, ({ one }) => ({
	airport: one(airports, {
		fields: [runways.airportId],
		references: [airports.id],
	}),
}));

export const flights = pgTable(
	"flights",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		aircraftId: uuid("aircraft_id")
			.references(() => aircraft.id, { onDelete: "cascade" })
			.notNull(),
		pic: uuid("pic").references(() => pilots.id, { onDelete: "set null" }),
		sic: uuid("sic").references(() => pilots.id, { onDelete: "set null" }),
		departureTime: timestamp("departure_time", {
			mode: "date",
			withTimezone: true,
		}),
		arrivalTime: timestamp("arrival_time", {
			mode: "date",
			withTimezone: true,
		}),
		originAirportId: uuid("origin_airport_id").references(() => airports.id, {
			onDelete: "set null",
		}),
		originRunwayId: uuid("origin_runway_id").references(() => runways.id, {
			onDelete: "set null",
		}),
		originRunwayIdentifier: text("origin_runway_identifier"),
		originTz: text("origin_tz"),
		originTzOffset: doublePrecision("origin_tz_offset"),
		destinationAirportId: uuid("destination_airport_id").references(
			() => airports.id,
			{ onDelete: "set null" },
		),
		destinationRunwayId: uuid("destination_runway_id").references(
			() => runways.id,
			{ onDelete: "set null" },
		),
		destinationRunwayIdentifier: text("destination_runway_identifier"),
		destinationTz: text("destination_tz"),
		destinationTzOffset: doublePrecision("destination_tz_offset"),
		filedDepartureTime: timestamp("filed_departure_time", {
			mode: "date",
			withTimezone: true,
		}),
		filedArrivalTime: timestamp("filed_arrival_time", {
			mode: "date",
			withTimezone: true,
		}),
		filedAirspeed: integer("filed_airspeed"),
		filedAltitude: integer("filed_altitude"),
		route: text("route"),
		routeDistance: doublePrecision("route_distance"),
		passengerBriefing: text("passenger_briefing"),
		pilotBriefing: text("pilot_briefing"),
		riskRecalculatedAt: timestamp("risk_recalculated_at", {
			mode: "date",
			withTimezone: true,
		}),
		riskFailedAt: timestamp("risk_failed_at", {
			mode: "date",
			withTimezone: true,
		}),
		trackCheckedAt: timestamp("track_checked_at", {
			mode: "date",
			withTimezone: true,
		}),
		trackFound: boolean("track_found"),
		fatigueCheckedAt: timestamp("fatigue_checked_at", {
			mode: "date",
			withTimezone: true,
		}),
		createdAt,
		externalId: text("external_id"),
		resolvedOutcome: text("resolved_outcome"),
		status: text("status").generatedAlwaysAs(
			sql`CASE
			WHEN resolved_outcome IS NOT NULL THEN resolved_outcome
			WHEN departure_time IS NOT NULL AND arrival_time IS NULL THEN 'en_route'
			WHEN departure_time IS NOT NULL AND arrival_time IS NOT NULL THEN 'complete'
			WHEN departure_time IS NULL AND filed_departure_time IS NOT NULL THEN 'scheduled'
			ELSE 'unknown'
		END`,
		),
		timeIndex: timestamp("time_index").generatedAlwaysAs(
			sql`COALESCE(departure_time, filed_departure_time)`,
		),
		updatedAt,
	},
	(table) => [
		index("aircraft_id_idx").on(table.aircraftId),
		index("arrival_time_idx").on(table.arrivalTime.desc()),
		index("external_id_idx").on(table.externalId),
		index("time_index_idx").on(table.timeIndex.desc()),
		index("flights_created_at_idx").on(table.createdAt),
	],
);

export type FlightInsertSchema = typeof flights.$inferInsert;
export type FlightSchema = typeof flights.$inferSelect;

export const metars = pgTable(
	"metars",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		text: text("text").notNull(),
		parsed: jsonb("parsed").$type<IMetarDated>(),
		stationId: text("station_id").notNull(),
		timestamp: timestamp("timestamp", {
			mode: "date",
			withTimezone: true,
		}).notNull(),
		flightId: uuid("flight_id").references(() => flights.id, {
			onDelete: "set null",
		}),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("metars_flight_id_idx").on(table.flightId),
		index("metars_timestamp_idx").on(table.timestamp),
		unique("unique_flight_station_timestamp").on(
			table.flightId,
			table.stationId,
			table.timestamp,
		),
	],
);

export type MetarInsertSchema = typeof metars.$inferInsert;
export const metarsRelations = relations(metars, ({ one }) => ({
	flight: one(flights, {
		fields: [metars.flightId],
		references: [flights.id],
	}),
}));

export const flightRisks = pgTable(
	"flight_risks",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		flightId: uuid("flight_id")
			.references(() => flights.id, { onDelete: "cascade" })
			.notNull(),
		type: varchar("type").notNull(),
		risk: doublePrecision("risk").notNull(),
		reasoning: text("reasoning"),
		input: jsonb("input"),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("flight_id_idx").on(table.flightId),
		unique("unique_flight_id_type").on(table.flightId, table.type),
	],
);

export const flightRisksRelations = relations(flightRisks, ({ one }) => ({
	flight: one(flights, {
		fields: [flightRisks.flightId],
		references: [flights.id],
	}),
}));

export type FlightRiskInsertSchema = typeof flightRisks.$inferInsert;
export type FlightRiskSchema = typeof flightRisks.$inferSelect;
export const flightProvider = pgEnum("flight_provider", [
	"fr24",
	"flightaware",
	"airsync",
	"adsbx",
	"garmin",
]);

export type FlightProvider = (typeof flightProvider.enumValues)[number];

export const providerFlights = pgTable(
	"provider_flights",
	{
		providerFlightId: text("provider_flight_id").primaryKey().notNull(),
		provider: flightProvider("provider").notNull(),
		flightId: uuid("flight_id")
			.references(() => flights.id, { onDelete: "cascade" })
			.notNull(),
		data: jsonb("data"),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("provider_flights_flight_id_idx").on(table.flightId),
		unique("unique_flight_id_provider").on(table.flightId, table.provider),
	],
);

export const providerFlightsRelations = relations(
	providerFlights,
	({ one }) => ({
		flight: one(flights, {
			fields: [providerFlights.flightId],
			references: [flights.id],
		}),
	}),
);

export const flightPulls = pgTable(
	"flight_pulls",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		aircraftId: uuid("aircraft_id")
			.references(() => aircraft.id, { onDelete: "cascade" })
			.notNull(),
		provider: flightProvider("provider").notNull(),
		windowStart: timestamp("window_start", {
			mode: "date",
			withTimezone: true,
		}).notNull(),
		windowEnd: timestamp("window_end", {
			mode: "date",
			withTimezone: true,
		}).notNull(),
		chunkStart: timestamp("chunk_start", {
			mode: "date",
			withTimezone: true,
		}).notNull(),
		chunkEnd: timestamp("chunk_end", {
			mode: "date",
			withTimezone: true,
		}).notNull(),
		state: text("state").$type<PullState>().notNull(),
		flightsReported: integer("flights_reported"),
		reportedIds: jsonb("reported_ids").$type<string[]>(),
		reason: text("reason"),
		error: text("error"),
		/** When the work was REQUESTED; replacement chunks inherit it so the gate keeps waiting. */
		requestedAt: timestamp("requested_at", {
			mode: "date",
			withTimezone: true,
		}).notNull(),
		settledAt: timestamp("settled_at", {
			mode: "date",
			withTimezone: true,
		}),
		createdAt,
	},
	(table) => [
		unique("flight_pulls_provider_aircraft_chunk_unique").on(
			table.provider,
			table.aircraftId,
			table.chunkStart,
			table.chunkEnd,
		),
		index("flight_pulls_aircraft_state_requested_idx").on(
			table.aircraftId,
			table.state,
			table.requestedAt,
		),
		index("flight_pulls_requested_at_idx").on(table.requestedAt),
		index("flight_pulls_reported_ids_idx").using(
			"gin",
			sql`${table.reportedIds} jsonb_path_ops`,
		),
	],
);

export type FlightPullSchema = typeof flightPulls.$inferSelect;

export const flightPullRelations = relations(flightPulls, ({ one }) => ({
	aircraft: one(aircraft, {
		fields: [flightPulls.aircraftId],
		references: [aircraft.id],
	}),
}));

/** The affirmed negative for a provider report that will never become a flight row. */
export const providerReportDrops = pgTable(
	"provider_report_drops",
	{
		provider: flightProvider("provider").notNull(),
		providerFlightId: text("provider_flight_id").notNull(),
		reason: text("reason").$type<ReportDropReason>().notNull(),
		detail: jsonb("detail"),
		createdAt,
	},
	(table) => [primaryKey({ columns: [table.provider, table.providerFlightId] })],
);

export type ProviderReportDropSchema = typeof providerReportDrops.$inferSelect;

export const flightEvents = pgTable(
	"flight_events",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		flightId: uuid("flight_id")
			.references(() => flights.id, { onDelete: "cascade" })
			.notNull(),
		type: text("type").notNull(),
		timestamp: timestamp("timestamp", {
			mode: "date",
			withTimezone: true,
		}).notNull(),
		location: geometry("location", { mode: "tuple", type: "point" }),
		altitude: doublePrecision("altitude"),
		speed: doublePrecision("speed"),
		heading: doublePrecision("heading"),
		value: doublePrecision("value"),
		data: jsonb("data").default({}).$type<FlightEventData>(),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("flight_events_flight_id_idx").on(table.flightId),
		index("flight_events_timestamp_idx").on(table.timestamp.desc()),
		index("flight_events_type_idx").on(table.type),
	],
);

export type FlightEvent = typeof flightEvents.$inferSelect;
export type FlightEventInsert = typeof flightEvents.$inferInsert;

export const flightEventsRelations = relations(flightEvents, ({ one }) => ({
	flight: one(flights, {
		fields: [flightEvents.flightId],
		references: [flights.id],
	}),
}));

export const rotorWingRiskMaps = pgTable("rotor_wing_risk_maps", {
	id: uuid().defaultRandom().primaryKey().notNull(),
	createdAt,
	updatedAt,
	aircraftId: uuid("aircraft_id").references(() => aircraft.id, { onDelete: "cascade" }).notNull(),
	flightId: uuid("flight_id").references(() => flights.id, { onDelete: "set null" }),
	location: geometry("location", { mode: "tuple", type: "point" }),
	timestamp: timestamp("timestamp", {
		mode: "string",
		withTimezone: true,
	}).notNull(),
	radiusNm: integer("radius_nm").notNull(),
	riskData: jsonb("risk_data").notNull(),
	overallRisk: doublePrecision("overall_risk").notNull(),
	actualRisk: doublePrecision("actual_risk"),
	actualRiskData: jsonb("actual_risk_data").$type<RotorWingActualRiskData>(),
}, (table) => [index("rotor_wing_risk_maps_flight_id_idx").on(table.flightId)]);

export const rotorWingRiskMapsRelations = relations(rotorWingRiskMaps, ({ one }) => ({
	aircraft: one(aircraft, {
		fields: [rotorWingRiskMaps.aircraftId],
		references: [aircraft.id],
	}),
	flight: one(flights, {
		fields: [rotorWingRiskMaps.flightId],
		references: [flights.id],
	}),
}));

export const incidents = pgTable("incidents", {
	id: uuid().defaultRandom().primaryKey().notNull(),
	createdAt,
	updatedAt,
	date: timestamp("date", { mode: "date", withTimezone: true }).notNull(),
	location: geometry("location", { mode: "tuple", type: "point" }),
	description: text("description"),
	summary: text("summary"),
	damage: text("damage"),
	fatalities: integer("fatalities"),
	injuries: integer("injuries"),
	aircraftId: uuid("aircraft_id").references(() => aircraft.id, { onDelete: "cascade" }).notNull(),
	flightId: uuid("flight_id").references(() => flights.id, { onDelete: "set null" }),
}, (table) => [
	index("incidents_aircraft_id_idx").on(table.aircraftId),
	index("incidents_flight_id_idx").on(table.flightId),
	index("incidents_date_idx").on(table.date),
]);

export const incidentsRelations = relations(incidents, ({ one, many }) => ({
	aircraft: one(aircraft, {
		fields: [incidents.aircraftId],
		references: [aircraft.id],
	}),
	flight: one(flights, {
		fields: [incidents.flightId],
		references: [flights.id],
	}),
	sourceReports: many(incidentSourceReports),
}));

export const incidentSource = pgEnum("incident_source", ["faa", "ntsb", "asn"]);

export const incidentSourceReports = pgTable("incident_source_reports", {
	id: uuid().defaultRandom().primaryKey().notNull(),
	createdAt,
	updatedAt,
	incidentId: uuid("incident_id").references(() => incidents.id, { onDelete: "cascade" }).notNull(),
	source: incidentSource("source").notNull(),
	externalId: text("external_id").notNull(),
	tailNumber: text("tail_number").notNull(),
	url: text("url"),
	fields: jsonb("fields").$type<IncidentSourceFields>(),
	raw: jsonb("raw"),
	sourceChangedAt: timestamp("source_changed_at", { mode: "date", withTimezone: true }),
	listingHash: text("listing_hash"),
	firstSeenAt: timestamp("first_seen_at", { mode: "date", withTimezone: true }).defaultNow().notNull(),
	lastSeenAt: timestamp("last_seen_at", { mode: "date", withTimezone: true }).defaultNow().notNull(),
	lastChangedAt: timestamp("last_changed_at", { mode: "date", withTimezone: true }).defaultNow().notNull(),
}, (table) => [
	unique("incident_source_reports_source_external_tail_unique").on(table.source, table.externalId, table.tailNumber),
	index("incident_source_reports_incident_id_idx").on(table.incidentId),
]);

export const incidentSourceReportsRelations = relations(incidentSourceReports, ({ one }) => ({
	incident: one(incidents, {
		fields: [incidentSourceReports.incidentId],
		references: [incidents.id],
	}),
}));

export const sanctionsScreenings = pgTable("sanctions_screenings", {
	id: uuid().defaultRandom().primaryKey().notNull(),
	createdAt,
	updatedAt,
	entityType: text("entity_type").notNull(),
	entityIdentifier: text("entity_identifier").notNull(),
	result: jsonb("result").notNull(),
	accountId: uuid("account_id").references(() => accounts.id, { onDelete: "set null" }),
});

export const sanctionsScreeningsRelations = relations(sanctionsScreenings, ({ one }) => ({
	account: one(accounts, {
		fields: [sanctionsScreenings.accountId],
		references: [accounts.id],
	}),
}));


export const flightComments = pgTable(
	"flight_comments",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		flightId: uuid("flight_id")
			.references(() => flights.id, { onDelete: "cascade" })
			.notNull(),
		comment: text("comment").notNull(),
		author: text("author").references(() => identities.key, {
			onDelete: "set null",
		}),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("flight_comments_flight_id_idx").on(table.flightId),
		index("flight_comments_author_idx").on(table.author),
	],
);

export const flightCommentRelations = relations(
	flightComments,
	({ one }) => ({
		flight: one(flights, {
			fields: [flightComments.flightId],
			references: [flights.id],
		}),
		author: one(identities, {
			fields: [flightComments.author],
			references: [identities.key],
		}),
	}),
);

export const flightsRelations = relations(flights, ({ one, many }) => ({
	aircraft: one(aircraft, {
		fields: [flights.aircraftId],
		references: [aircraft.id],
	}),
	pic: one(pilots, {
		fields: [flights.pic],
		references: [pilots.id],
		relationName: "flight_pic",
	}),
	sic: one(pilots, {
		fields: [flights.sic],
		references: [pilots.id],
		relationName: "flight_sic",
	}),
	destinationAirport: one(airports, {
		fields: [flights.destinationAirportId],
		references: [airports.id],
		relationName: "ap_destination",
	}),
	originAirport: one(airports, {
		fields: [flights.originAirportId],
		references: [airports.id],
		relationName: "ap_origin",
	}),
	destinationRunway: one(runways, {
		fields: [flights.destinationRunwayId],
		references: [runways.id],
		relationName: "rw_destination",
	}),
	originRunway: one(runways, {
		fields: [flights.originRunwayId],
		references: [runways.id],
		relationName: "rw_origin",
	}),
	flightRisks: many(flightRisks),
	metars: many(metars),
	providersFlights: many(providerFlights),
	events: many(flightEvents),
	incidents: many(incidents),
	comments: many(flightComments),
	rotorWingRiskMaps: many(rotorWingRiskMaps),
}));

export const flightLogs = pgTable(
	"flight_logs",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		createdAt,
		updatedAt,
		submissionId: uuid("submission_id")
			.references(() => submissions.id, { onDelete: "cascade" })
			.notNull(),
		provider: text("provider").notNull(),
		parsed: jsonb("parsed"),
	},
	(table) => [index("submission_id_idx").on(table.submissionId)],
);

export const flightLogsRelations = relations(flightLogs, ({ one }) => ({
	submission: one(submissions, {
		fields: [flightLogs.submissionId],
		references: [submissions.id],
	}),
}));

export const airways = pgTable("airways", {
	id: text("id").primaryKey().notNull(),
	airwayString: text("airway_string").notNull(),
	awyDesignation: text("awy_designation").notNull(),
	awyLocation: text("awy_location").notNull(),
	effDate: date("eff_date").notNull(),
	updateDate: date("update_date").notNull(),
	regulatory: boolean("regulatory").notNull().default(false),
	remark: text("remark"),
	createdAt,
	updatedAt,
});

export type AirwaysInsertSchema = typeof airways.$inferInsert;

export const navFixes = pgTable("nav_fixes", {
	id: text("id").primaryKey().notNull(),
	type: text("type").notNull(),
	lat: doublePrecision("lat").notNull(),
	lon: doublePrecision("lon").notNull(),
	icaoRegionCode: text("icao_region_code"),
	stateCode: text("state_code"),
	countryCode: text("country_code"),
	effDate: date("eff_date").notNull(),
	createdAt,
	updatedAt,
});

export type NavFixInsertSchema = typeof navFixes.$inferInsert;

export const navaids = pgTable(
	"navaids",
	{
		id: text("id").notNull(),
		navType: text("nav_type").notNull(),
		stateCode: text("state_code").notNull(),
		city: text("city").notNull(),
		name: text("name"),
		status: text("status"),
		lat: doublePrecision("lat").notNull(),
		lon: doublePrecision("lon").notNull(),
		countryCode: text("country_code"),
		effDate: date("eff_date").notNull(),
		createdAt,
		updatedAt,
	},
	(table) => [
		primaryKey({
			columns: [table.id, table.navType, table.stateCode, table.city],
		}),
		index("navaids_id_idx").on(table.id),
	],
);

export type NavaidInsertSchema = typeof navaids.$inferInsert;

export const partnerApiKeys = pgTable(
	"partner_api_keys",
	{
		partnerSlug: text("partner_slug").notNull(),
		key: text("key").notNull(),
		disabledAt: timestamp("disabled_at", { mode: "date", withTimezone: true }),
		createdAt,
		updatedAt,
	},
	(table) => [
		primaryKey({ columns: [table.partnerSlug, table.key] }),
		index("partner_slug_idx").on(table.partnerSlug),
		index("key_idx").on(table.key),
	],
);

export const aircraftRelations = relations(aircraft, ({ many, one }) => ({
	type: one(aircraftTypes, {
		fields: [aircraft.type],
		references: [aircraftTypes.icaoCode],
	}),
	claims: many(claims),
	policiesAircraft: many(policiesAircraft),
	submissionsAircraft: many(submissionsAircraft),
	flights: many(flights),
	configs: many(aircraftConfigs),
	incidents: many(incidents),
	rotorWingRiskMaps: many(rotorWingRiskMaps),
	policyEndorsements: many(policyEndorsements),
}));

export const quoteRelations = relations(quotes, ({ one, many }) => ({
	submission: one(submissions, {
		fields: [quotes.submissionId],
		references: [submissions.id],
	}),
	endorsements: many(quoteEndorsements),
	policies: many(policies),
	documents: many(submissionDocuments),
	decisions: many(underwritingDecisions),
	decisionJobs: many(submissionDecisionJobs),
}));

export const quoteEndorsementsRelations = relations(
	quoteEndorsements,
	({ one, many }) => ({
		quote: one(quotes, {
			fields: [quoteEndorsements.quoteId],
			references: [quotes.id],
		}),
		policyCerts: many(policyCerts),
		policyEndorsements: many(policyEndorsements),
	}),
);

export const stations = pgTable(
	"stations",
	{
		icaoId: text("icao_id").primaryKey().notNull(),
		iataId: text("iata_id"),
		faaId: text("faa_id"),
		wmoId: text("wmo_id"),
		location: geometry("location", { mode: "tuple", type: "point" }),
		elev: doublePrecision("elev").notNull(),
		site: text("site").notNull(),
		state: text("state"),
		country: text("country"),
		priority: integer("priority").notNull(),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("stations_location_gist_idx").using("gist", table.location),
	],
);

export const airportStations = pgTable(
	"airport_stations",
	{
		airportId: uuid("airport_id").references(() => airports.id, { onDelete: "cascade" }).notNull(),
		stationId: text("station_id").references(() => stations.icaoId, { onDelete: "cascade" }).notNull(),
		distance: doublePrecision("distance"),
	},
	(table) => [
		primaryKey({ columns: [table.airportId, table.stationId] }),
		index("airport_stations_airport_id_idx").on(table.airportId),
		index("airport_stations_station_id_idx").on(table.stationId),
	],
);

export type AirportStationSchema = typeof airportStations.$inferSelect;

export const marketPricing = pgTable(
	"market_pricing",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		submissionId: uuid("submission_id")
			.references(() => submissions.id, { onDelete: "cascade" })
			.notNull(),
		price: doublePrecision("price").notNull(),
		market: text("market").notNull(),
		limit: doublePrecision("limit"),
		incumbent: boolean("incumbent").notNull().default(false),
		notes: text("notes"),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("market_pricing_submission_id_idx").on(table.submissionId),
	],
);

export const marketPricingRelations = relations(marketPricing, ({ one }) => ({
	submission: one(submissions, {
		fields: [marketPricing.submissionId],
		references: [submissions.id],
	}),
}));

export const submissionsRelations = relations(submissions, ({ many, one }) => ({
	submissionsAircraft: many(submissionsAircraft),
	assignedTo: one(users, {
		fields: [submissions.assignedTo],
		references: [users.id],
	}),
	broker: one(brokers, {
		fields: [submissions.broker],
		references: [brokers.email],
	}),
	account: one(accounts, {
		fields: [submissions.accountId],
		references: [accounts.id],
	}),
	inboxMessage: one(inboxMessages, {
		fields: [submissions.inboxMessageId],
		references: [inboxMessages.id],
	}),
	flightLogs: many(flightLogs),
	quotes: many(quotes),
	comments: many(submissionComments),
	indications: many(submissionIndications),
	decisions: many(underwritingDecisions),
	decisionJobs: many(submissionDecisionJobs),
	activities: many(submissionActivity),
	actions: many(submissionActions),
	underwritingSteps: many(submissionUnderwritingSteps),
	marketPricing: many(marketPricing),
	reminders: many(submissionReminders),
	tasks: many(submissionTasks),
	documents: many(submissionDocuments),
	pilotCompetencyRatings: many(pilotCompetencyRatings),
	submissionsLeads: many(submissionsLeads),
	previousSubmission: one(submissions, {
		fields: [submissions.previousSubmissionId],
		references: [submissions.id],
		relationName: "submissionRenewalLineage",
	}),
	generatedRenewals: many(submissions, { relationName: "submissionRenewalLineage" }),
	mocoOpportunities: many(mocoOpportunities),
}));

export const accountsRelations = relations(accounts, ({ many }) => ({
	policies: many(policies),
	submissions: many(submissions),
	aircraft: many(aircraft),
	sanctionsScreenings: many(sanctionsScreenings),
}));

export const eventSignups = pgTable(
	"event_signups",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		email: text("email").notNull(),
		name: text("name").notNull(),
		phone: text("phone"),
		broker: text("broker"),
		tailNumber: text("tail_number"),
		event: text("event").notNull(),
		createdAt,
		updatedAt,
	},
);

export const airportStationsRelations = relations(airportStations, ({ one }) => ({
	airport: one(airports, {
		fields: [airportStations.airportId],
		references: [airports.id],
	}),
	station: one(stations, {
		fields: [airportStations.stationId],
		references: [stations.icaoId],
	}),
}));

export const borChecks = pgTable("bor_checks", {
	createdAt,
	updatedAt,
	partnerSlug: text("partner_slug").notNull(),
	namedInsured: text("named_insured").notNull(),
	tailNumbers: text("tail_numbers").array().default(sql`'{}'::text[]`).notNull(),
	date: date("date", { mode: "string" }).notNull(),
	borRequired: boolean("bor_required").notNull().default(false),
}, (table) => [
	index("bor_checks_partner_slug_idx").on(table.partnerSlug),
	unique(
		"bor_checks_unique_combo_idx",
	)
		.on(
			table.partnerSlug,
			table.namedInsured,
			table.date,
			table.tailNumbers,
		),
]);

export const aircraftConfigsRelations = relations(aircraftConfigs, ({ one }) => ({
	aircraft: one(aircraft, {
		fields: [aircraftConfigs.aircraftId],
		references: [aircraft.id],
	}),
}));

export const directMailLetters = pgTable("direct_mail_letters", {
	id: uuid().defaultRandom().primaryKey().notNull(),
	createdAt,
	updatedAt,
	policyId: text("policy_id").references(() => policies.id, { onDelete: "set null" }),
	externalId: text("external_id").notNull(),
	type: text("type").notNull(),
	impotencyKey: text("impotency_key").notNull().unique(),
}, (table) => [
	index("direct_mail_letters_policy_id_idx").on(table.policyId),
	index("direct_mail_letters_impotency_key_idx").on(table.impotencyKey),
]);

export const directMailLettersRelations = relations(directMailLetters, ({ one }) => ({
	policy: one(policies, {
		fields: [directMailLetters.policyId],
		references: [policies.id],
	}),
}));

export const memoryKind = pgEnum("memory_kind", ["heuristic", "rubric", "example", "counterexample", "idea", "other"]);

export const memories = pgTable("memories", {
	id: uuid().defaultRandom().primaryKey().notNull(),
	createdAt,
	updatedAt,
	statement: text("statement").notNull(),
	embedding: vector("embedding", { dimensions: 1536 }),
	embeddingModel: text("embedding_model").notNull(),
	type: text("type").notNull(),
	kind: memoryKind("kind").notNull(),
}, (table) => [
	index("memories_embedding_vector_idx").using("hnsw", table.embedding.op("vector_l2_ops")),
	index("memories_type_idx").on(table.type),
	index("memories_kind_idx").on(table.kind),
]);

export const memorySources = pgTable("memory_sources", {
	id: uuid().defaultRandom().primaryKey().notNull(),
	memoryId: uuid("memory_id").references(() => memories.id, { onDelete: "cascade" }).notNull(),
	sourceType: text("source_type").notNull(),
	sourceId: text("source_id").notNull(),
	createdAt,
}, (table) => [
	index("memory_sources_memory_id_idx").on(table.memoryId),
	index("memory_sources_source_type_idx").on(table.sourceType),
	index("memory_sources_source_id_idx").on(table.sourceId),
	unique("memory_sources_memory_id_source_id_idx").on(table.memoryId, table.sourceId),
]);

export const memoryRelations = relations(memories, ({ many }) => ({
	sources: many(memorySources),
}));

export const memorySourcesRelations = relations(memorySources, ({ one }) => ({
	memory: one(memories, {
		fields: [memorySources.memoryId],
		references: [memories.id],
	}),
}));

/** QuickBooks environment connection – one row per environment (production | sandbox). Stores realmId + refresh token for /quickbooks-auth. */
export const QUICKBOOKS_ENVIRONMENT_VALUES = ["production", "sandbox"] as const;
export type QuickbooksEnvironment = (typeof QUICKBOOKS_ENVIRONMENT_VALUES)[number];

export const quickbooksEnvironmentConnection = pgTable(
	"quickbooks_environment_connection",
	{
		id: serial("id").primaryKey(),
		environment: varchar("environment", { length: 32 }).notNull(),
		connectionOptionsEncrypted: text("connection_options_encrypted"),
		refreshTokenEncrypted: text("refresh_token_encrypted"),
		createdAt,
		updatedAt,
	},
	(table) => [
		unique("quickbooks_environment_connection_environment_unique").on(table.environment),
	],
);

// ─── FOQA / SMS ──────────────────────────────────────────────────────────────

export const foqaMemberRole = pgEnum("foqa_member_role", ["admin", "pilot"]);
export type FoqaMemberRole = (typeof foqaMemberRole.enumValues)[number];

export const foqaAccessLevel = pgEnum("foqa_access_level", ["aggregate", "full"]);
export type FoqaAccessLevel = (typeof foqaAccessLevel.enumValues)[number];

export const foqaBand = pgEnum("foqa_band", ["green", "yellow", "red", "na"]);
export type FoqaBandEnum = (typeof foqaBand.enumValues)[number];

export const fleets = pgTable("fleets", {
	id: uuid().defaultRandom().primaryKey().notNull(),
	name: text("name").notNull(),
	createdAt,
	updatedAt,
});

export type FleetSchema = typeof fleets.$inferSelect;
export type FleetInsertSchema = typeof fleets.$inferInsert;

export const fleetAircraft = pgTable(
	"fleet_aircraft",
	{
		fleetId: uuid("fleet_id")
			.references(() => fleets.id, { onDelete: "cascade" })
			.notNull(),
		aircraftId: uuid("aircraft_id")
			.references(() => aircraft.id, { onDelete: "cascade" })
			.notNull(),
		createdAt,
	},
	(table) => [primaryKey({ columns: [table.fleetId, table.aircraftId] })],
);

export type FleetAircraftSchema = typeof fleetAircraft.$inferSelect;
export type FleetAircraftInsertSchema = typeof fleetAircraft.$inferInsert;

export const fleetMembers = pgTable(
	"fleet_members",
	{
		fleetId: uuid("fleet_id")
			.references(() => fleets.id, { onDelete: "cascade" })
			.notNull(),
		userId: uuid("user_id")
			.references(() => users.id, { onDelete: "cascade" })
			.notNull(),
		role: foqaMemberRole("role").notNull(),
		access: foqaAccessLevel("access").notNull().default("aggregate"),
		createdAt,
		updatedAt,
	},
	(table) => [primaryKey({ columns: [table.fleetId, table.userId] })],
);

export type FleetMemberSchema = typeof fleetMembers.$inferSelect;
export type FleetMemberInsertSchema = typeof fleetMembers.$inferInsert;

export const tailClaimStatus = pgEnum("tail_claim_status", ["pending", "approved", "rejected"]);
export type TailClaimStatus = (typeof tailClaimStatus.enumValues)[number];

/** Only created when a tail is contested — an unclaimed tail attaches immediately. */
export const tailClaims = pgTable(
	"tail_claims",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		fleetId: uuid("fleet_id")
			.references(() => fleets.id, { onDelete: "cascade" })
			.notNull(),
		aircraftId: uuid("aircraft_id")
			.references(() => aircraft.id, { onDelete: "cascade" })
			.notNull(),
		tailNumber: varchar("tail_number", { length: 30 }).notNull(),
		requestedBy: uuid("requested_by").references(() => users.id, { onDelete: "set null" }),
		status: tailClaimStatus("status").notNull().default("pending"),
		resolvedBy: uuid("resolved_by").references(() => users.id, { onDelete: "set null" }),
		resolvedAt: timestamp("resolved_at", { withTimezone: true, mode: "date" }),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("tail_claims_fleet_id_idx").on(table.fleetId),
		index("tail_claims_aircraft_id_idx").on(table.aircraftId),
		index("tail_claims_status_idx").on(table.status),
		// One live contest per tail at a time — a second claim on the same aircraft
		// while one is pending reuses this row instead of racing it.
		uniqueIndex("tail_claims_aircraft_pending_unique")
			.on(table.aircraftId)
			.where(sql`${table.status} = 'pending'`),
	],
);

export type TailClaimSchema = typeof tailClaims.$inferSelect;
export type TailClaimInsertSchema = typeof tailClaims.$inferInsert;

export const tailPairingStatus = pgEnum("tail_pairing_status", ["attempted", "succeeded", "failed", "revoked"]);
export type TailPairingStatus = (typeof tailPairingStatus.enumValues)[number];

/** Distinguishes "paired" from "never attempted". One row per (aircraft, provider); a retry
 *  updates it, so lastError is the latest attempt. `provider` is text, not an enum, so the
 *  next provider needs no type migration. */
export const tailPairings = pgTable(
	"tail_pairings",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		aircraftId: uuid("aircraft_id")
			.references(() => aircraft.id, { onDelete: "cascade" })
			.notNull(),
		tailNumber: varchar("tail_number", { length: 30 }).notNull(),
		provider: text("provider").$type<"airsync">().notNull(),
		status: tailPairingStatus("status").notNull().default("attempted"),
		lastError: text("last_error"),
		/** Identifies the in-flight attempt so a slow one cannot resolve over a newer one. */
		attemptId: uuid("attempt_id").defaultRandom().notNull(),
		attemptedAt: timestamp("attempted_at", { withTimezone: true, mode: "date" }).notNull().defaultNow(),
		succeededAt: timestamp("succeeded_at", { withTimezone: true, mode: "date" }),
		failedAt: timestamp("failed_at", { withTimezone: true, mode: "date" }),
		revokedAt: timestamp("revoked_at", { withTimezone: true, mode: "date" }),
		requestedBy: uuid("requested_by").references(() => users.id, { onDelete: "set null" }),
		createdAt,
		updatedAt,
	},
	(table) => [
		uniqueIndex("tail_pairings_aircraft_provider_unique").on(table.aircraftId, table.provider),
		index("tail_pairings_status_idx").on(table.status),
	],
);

export type TailPairingSchema = typeof tailPairings.$inferSelect;
export type TailPairingInsertSchema = typeof tailPairings.$inferInsert;

export const foqaThresholds = pgTable(
	"foqa_thresholds",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		aircraftModel: text("aircraft_model").notNull(),
		// Cohort-scoped thresholds: null = the global default set; a fleet id = that cohort's
		// overrides. Scoring resolves (fleet,model)→(fleet,default)→(global,model)→(global,default).
		fleetId: uuid("fleet_id").references(() => fleets.id, { onDelete: "cascade" }),
		version: integer("version").notNull().default(1),
		active: boolean("active").notNull().default(true),
		config: jsonb("config")
			.$type<FoqaThresholdConfig>()
			.notNull()
			.default(sql`'[]'::jsonb`),
		policy: jsonb("policy").$type<FoqaScoringPolicy>(),
		createdBy: uuid("created_by").references(() => users.id, {
			onDelete: "set null",
		}),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("foqa_thresholds_aircraft_model_idx").on(table.aircraftModel),
		index("foqa_thresholds_active_idx").on(table.active),
		index("foqa_thresholds_fleet_idx").on(table.fleetId),
		// Global rows (fleet_id null) unique per (model, version); cohort rows per
		// (fleet, model, version). Partial indexes so the two namespaces don't collide.
		uniqueIndex("foqa_thresholds_global_model_version_unique")
			.on(table.aircraftModel, table.version)
			.where(sql`${table.fleetId} is null`),
		uniqueIndex("foqa_thresholds_fleet_model_version_unique")
			.on(table.fleetId, table.aircraftModel, table.version)
			.where(sql`${table.fleetId} is not null`),
	],
);

export type FoqaThresholdSchema = typeof foqaThresholds.$inferSelect;
export type FoqaThresholdInsertSchema = typeof foqaThresholds.$inferInsert;

/**
 * Lets one scoring profile serve several aircraft models: `aircraft_model` resolves
 * `profile_model`'s thresholds and flags instead of its own.
 *
 * SCORING CONFIG ONLY. Deliberately not applied to foqa_model_profiles — two models can
 * share a scorecard while having genuinely different Vref tables and landing distances, so
 * redirecting the performance lookup would silently score against the wrong airframe.
 */
export const foqaScoringAliases = pgTable(
	"foqa_scoring_aliases",
	{
		aircraftModel: text("aircraft_model").primaryKey().notNull(),
		profileModel: text("profile_model").notNull(),
		createdBy: uuid("created_by").references(() => users.id, { onDelete: "set null" }),
		createdAt,
		updatedAt,
	},
	(table) => [index("foqa_scoring_aliases_profile_idx").on(table.profileModel)],
);

export type FoqaScoringAliasSchema = typeof foqaScoringAliases.$inferSelect;

/**
 * Per-airframe data (Vref table, ZFW, V-speeds), keyed by the same model key that resolves
 * scoring config. Deliberately NOT fleet-scoped: where a cohort's bands sit is policy, but
 * an airframe's Vref table is physics and must not fork per cohort.
 *
 * Versioned like foqa_thresholds/foqa_flags so a deploy or a reseed cannot silently rescore
 * history: reads take the highest `active` version, prior versions stay as an audit trail.
 * That is why the primary key is a surrogate id — aircraft_model now identifies a profile's
 * version chain, not a row. One unique index rather than the fleet-partial pair those tables
 * carry, since there is no cohort namespace here.
 */
export const foqaModelProfiles = pgTable(
	"foqa_model_profiles",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		aircraftModel: text("aircraft_model").notNull(),
		label: text(),
		version: integer("version").notNull().default(1),
		active: boolean("active").notNull().default(true),
		vrefProfile: jsonb("vref_profile").$type<AircraftVrefProfile>().notNull(),
		createdBy: uuid("created_by").references(() => users.id, { onDelete: "set null" }),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("foqa_model_profiles_aircraft_model_idx").on(table.aircraftModel),
		index("foqa_model_profiles_active_idx").on(table.active),
		uniqueIndex("foqa_model_profiles_model_version_unique").on(table.aircraftModel, table.version),
	],
);

export type FoqaModelProfileSchema = typeof foqaModelProfiles.$inferSelect;
export type FoqaModelProfileInsertSchema = typeof foqaModelProfiles.$inferInsert;

/**
 * Per-model AFM lookups (Vref, landing distance, …) — the identity row above holds scalars,
 * this holds the tables. Two forms: `grid` (nested `cells` in axis order, null = the AFM's
 * not-permitted cells) and `reference_coefficients` (one `reference` value plus signed
 * per-unit deltas), because the second cannot be flattened into the first without inventing
 * values. See schema/foqa-performance.ts.
 *
 * Versioned like foqa_thresholds/foqa_flags, and like foqa_model_profiles NOT fleet-scoped:
 * this is airframe physics, not cohort policy. `applicability` is the per-airframe layer
 * (MSN range, engine variant, specific tails) that splits valid cells within one type.
 */
export const foqaPerformanceTables = pgTable(
	"foqa_performance_tables",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		// Not an FK: foqa_model_profiles is versioned, so aircraft_model alone is no longer
		// unique there and Postgres cannot reference it. Resolved through the active profile.
		aircraftModel: text("aircraft_model").notNull(),
		kind: text("kind").$type<FoqaPerformanceKind>().notNull(),
		form: text("form").$type<FoqaPerformanceForm>().notNull(),
		config: jsonb("config")
			.$type<FoqaPerformanceConfig>()
			.notNull()
			.default(sql`'{}'::jsonb`),
		axes: jsonb("axes")
			.$type<FoqaPerformanceAxis[]>()
			.notNull()
			.default(sql`'[]'::jsonb`),
		cells: jsonb("cells").$type<FoqaPerformanceCells>(),
		reference: jsonb("reference").$type<FoqaPerformanceReference>(),
		result: jsonb("result").$type<FoqaPerformanceResult>(),
		applicability: jsonb("applicability")
			.$type<FoqaPerformanceApplicability>()
			.notNull()
			.default(sql`'{}'::jsonb`),
		source: jsonb("source").$type<FoqaPerformanceSource>(),
		version: integer("version").notNull().default(1),
		active: boolean("active").notNull().default(true),
		createdBy: uuid("created_by").references(() => users.id, { onDelete: "set null" }),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("foqa_performance_tables_model_kind_idx").on(table.aircraftModel, table.kind),
		index("foqa_performance_tables_active_idx").on(table.active),
		// A table's identity is what it was published for, not a name someone typed: one row
		// per (model, kind, configuration, applicability) at a version.
		uniqueIndex("foqa_performance_tables_identity_unique")
			.on(table.aircraftModel, table.kind, table.config, table.applicability, table.version),
		// At most one live row per chain, as a DB fact — the write paths also serialize on a
		// FOR UPDATE chain lock so a lost race re-reads instead of hitting 23505.
		uniqueIndex("foqa_performance_tables_active_identity_unique")
			.on(table.aircraftModel, table.kind, table.config, table.applicability)
			.where(sql`${table.active} = true`),
		// …which only holds if the identity payloads have one encoding. Resolution reads an
		// explicit null key as "does not constrain", but jsonb equality does not, so `{}` and
		// `{"engine":null}` would slip past the index as two rival rows for the same airframe.
		// An empty tailNumbers array says the same thing and survives jsonb_strip_nulls, so it
		// needs rejecting by name — it is the only array key in either payload.
		check(
			"foqa_performance_tables_identity_null_free",
			sql`${table.config} = jsonb_strip_nulls(${table.config})
				and ${table.applicability} = jsonb_strip_nulls(${table.applicability})
				and (${table.applicability} -> 'tailNumbers') is distinct from '[]'::jsonb`,
		),
	],
);

export type FoqaPerformanceTableSchema = typeof foqaPerformanceTables.$inferSelect;
export type FoqaPerformanceTableInsertSchema = typeof foqaPerformanceTables.$inferInsert;

export const foqaFlags = pgTable(
	"foqa_flags",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		fleetId: uuid("fleet_id").references(() => fleets.id, { onDelete: "cascade" }),
		// Mirrors foqa_thresholds so flags resolve on the same (fleet, model) chain; rows
		// predating model scoping carry the universal 'default' key.
		aircraftModel: text("aircraft_model").notNull().default("default"),
		version: integer("version").notNull().default(1),
		active: boolean("active").notNull().default(true),
		flags: jsonb("flags")
			.$type<FoqaFlagConfig>()
			.notNull()
			.default(sql`'[]'::jsonb`),
		createdBy: uuid("created_by").references(() => users.id, {
			onDelete: "set null",
		}),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("foqa_flags_active_idx").on(table.active),
		index("foqa_flags_fleet_idx").on(table.fleetId),
		index("foqa_flags_aircraft_model_idx").on(table.aircraftModel),
		uniqueIndex("foqa_flags_global_model_version_unique")
			.on(table.aircraftModel, table.version)
			.where(sql`${table.fleetId} is null`),
		uniqueIndex("foqa_flags_fleet_model_version_unique")
			.on(table.fleetId, table.aircraftModel, table.version)
			.where(sql`${table.fleetId} is not null`),
	],
);

export type FoqaFlagsSchema = typeof foqaFlags.$inferSelect;
export type FoqaFlagsInsertSchema = typeof foqaFlags.$inferInsert;

/**
 * Cohort interventions — dated safety actions (training updates, SOP changes, briefings) a
 * cohort admin records so their effect on the fleet trend is visible. Rendered as dated
 * markers on the aggregate flag-rate trend and used as context for the finding engine
 * (the "we acted → the fleet improved" closed loop). Fleet-scoped; `phase` optionally ties
 * an intervention to a flight phase (matches FoqaPhase) so a category-filtered chart can show
 * only the relevant markers.
 */
export const foqaInterventions = pgTable(
	"foqa_interventions",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		fleetId: uuid("fleet_id")
			.references(() => fleets.id, { onDelete: "cascade" })
			.notNull(),
		occurredOn: date("occurred_on", { mode: "string" }).notNull(),
		title: text("title").notNull(),
		description: text("description"),
		/** Optional flight-phase association (FoqaPhase: general|departure|arrival|approach_landing). */
		phase: text("phase"),
		createdBy: uuid("created_by").references(() => users.id, {
			onDelete: "set null",
		}),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("foqa_interventions_fleet_idx").on(table.fleetId),
		index("foqa_interventions_occurred_on_idx").on(table.occurredOn),
	],
);

export type FoqaInterventionSchema = typeof foqaInterventions.$inferSelect;
export type FoqaInterventionInsertSchema = typeof foqaInterventions.$inferInsert;

export const foqaDebriefs = pgTable(
	"foqa_debriefs",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		flightId: uuid("flight_id")
			.references(() => flights.id, { onDelete: "cascade" })
			.notNull(),
		aircraftId: uuid("aircraft_id")
			.references(() => aircraft.id, { onDelete: "cascade" })
			.notNull(),
		thresholdId: uuid("threshold_id").references(() => foqaThresholds.id, {
			onDelete: "set null",
		}),
		/** The kind:'vref' foqa_performance_tables row that produced `computedVref`. */
		performanceTableId: uuid("performance_table_id").references(() => foqaPerformanceTables.id, {
			onDelete: "set null",
		}),
		score: integer("score").notNull(),
		safetyEventCount: integer("safety_event_count").notNull().default(0),
		/** Operative Vref scoring used (override → computed). */
		vref: doublePrecision("vref"),
		/** Computed Vref baseline (bus/CJP estimate) — the value an override falls back to
		 *  when cleared. Distinct from `vref`, which reflects any active override. */
		computedVref: doublePrecision("computed_vref"),
		approachType: text("approach_type"),
		summary: jsonb("summary")
			.$type<FoqaDebriefSummary>()
			.notNull()
			.default(sql`'[]'::jsonb`),
		flags: jsonb("flags")
			.$type<FoqaDebriefFlags>()
			.notNull()
			.default(sql`'[]'::jsonb`),
		// Raw analysis output (the inputs to scoring) — lets a human edit Vref/approach
		// type and re-score WITHOUT re-running analysis. Nullable: pre-existing rows and
		// flights with no resolvable landing have no measurements.
		measurements: jsonb("measurements").$type<FoqaMeasurements>(),
		// Versioning: each edit bumps the version and snapshots the prior state into
		// foqa_debrief_revisions. `source` = how the CURRENT row was produced.
		version: integer("version").notNull().default(1),
		source: text("source").notNull().default("computed"),
		/** Manual input overrides applied on top of the computed values (§7.1). */
		overrides: jsonb("overrides")
			.$type<FoqaDebriefOverrides>()
			.notNull()
			.default(sql`'{}'::jsonb`),
		editedBy: uuid("edited_by").references(() => users.id, { onDelete: "set null" }),
		editedAt: timestamp("edited_at", { withTimezone: true, mode: "date" }),
		/** When the flight actually happened (flights.departure_time), vs createdAt = ingestion time. */
		flightTime: timestamp("flight_time", { withTimezone: true, mode: "date" }),
		createdAt,
		updatedAt,
	},
	(table) => [
		unique("foqa_debriefs_flight_id_unique").on(table.flightId),
		index("foqa_debriefs_aircraft_id_idx").on(table.aircraftId),
		index("foqa_debriefs_flight_time_idx").on(table.flightTime),
	],
);

export type FoqaDebriefSchema = typeof foqaDebriefs.$inferSelect;
export type FoqaDebriefInsertSchema = typeof foqaDebriefs.$inferInsert;

/**
 * Append-only history of superseded debrief states. Each time a debrief changes (a
 * worker reprocess or a human input edit), the PRIOR state is snapshotted here before
 * the current row is overwritten — so the original/computed values are never lost and a
 * correction's provenance is auditable (foundation for epic B's re-render + re-send).
 */
export const foqaDebriefRevisions = pgTable(
	"foqa_debrief_revisions",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		debriefId: uuid("debrief_id")
			.references(() => foqaDebriefs.id, { onDelete: "cascade" })
			.notNull(),
		flightId: uuid("flight_id")
			.references(() => flights.id, { onDelete: "cascade" })
			.notNull(),
		version: integer("version").notNull(),
		source: text("source").notNull(),
		score: integer("score").notNull(),
		safetyEventCount: integer("safety_event_count").notNull().default(0),
		vref: doublePrecision("vref"),
		computedVref: doublePrecision("computed_vref"),
		performanceTableId: uuid("performance_table_id").references(() => foqaPerformanceTables.id, {
			onDelete: "set null",
		}),
		approachType: text("approach_type"),
		overrides: jsonb("overrides")
			.$type<FoqaDebriefOverrides>()
			.notNull()
			.default(sql`'{}'::jsonb`),
		summary: jsonb("summary")
			.$type<FoqaDebriefSummary>()
			.notNull()
			.default(sql`'[]'::jsonb`),
		measurements: jsonb("measurements").$type<FoqaMeasurements>(),
		/** Who produced this state (null for machine/computed). */
		editedBy: uuid("edited_by").references(() => users.id, { onDelete: "set null" }),
		createdAt,
	},
	(table) => [
		index("foqa_debrief_revisions_debrief_id_idx").on(table.debriefId),
		index("foqa_debrief_revisions_flight_id_idx").on(table.flightId),
	],
);

export type FoqaDebriefRevisionSchema = typeof foqaDebriefRevisions.$inferSelect;
export type FoqaDebriefRevisionInsertSchema = typeof foqaDebriefRevisions.$inferInsert;

/**
 * Product-aware invitation state machine layered on the pw_reset_tokens credential
 * mechanism. Identity-keyed (a PERSON, account or not); product buckets are never
 * stored — this records the pending grant (FOQA fleet membership or FRAT access) to
 * apply when the invite is accepted.
 */
export const invitations = pgTable(
	"invitations",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		identityKey: text("identity_key")
			.references(() => identities.key, { onDelete: "cascade" })
			.notNull(),
		product: text("product").$type<"foqa" | "frat">().notNull(),
		fleetId: uuid("fleet_id").references(() => fleets.id, { onDelete: "cascade" }),
		/** Requested FOQA role/access to grant on accept; only meaningful for product `foqa`. */
		role: text("role").$type<FoqaMemberRole>(),
		access: text("access").$type<FoqaAccessLevel>(),
		invitedBy: uuid("invited_by").references(() => users.id, { onDelete: "set null" }),
		status: text("status")
			.$type<"pending" | "accepted" | "revoked">()
			.notNull()
			.default("pending"),
		acceptedAt: timestamp("accepted_at", { withTimezone: true, mode: "date" }),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("invitations_identity_key_idx").on(table.identityKey),
		index("invitations_fleet_id_idx").on(table.fleetId),
		uniqueIndex("invitations_fleet_identity_pending_unique")
			.on(table.fleetId, table.identityKey)
			.where(sql`${table.status} = 'pending'`),
	],
);

export type InvitationSchema = typeof invitations.$inferSelect;
export type InvitationInsertSchema = typeof invitations.$inferInsert;

/**
 * Per-aircraft FOQA settings. `pullFromPolicy` (default true) controls whether debrief
 * owners are resolved LIVE from the insurance graph (aircraft → policies_aircraft → policy →
 * users_policies → users) at send/ACL time. Set false to opt an aircraft out of policy-based
 * recipients (then only the explicit `foqa_aircraft_owners` links apply). A missing row = the
 * default (pull). No offline sync — ownership is always resolved at runtime.
 */
export const foqaAircraft = pgTable("foqa_aircraft", {
	aircraftId: uuid("aircraft_id")
		.primaryKey()
		.references(() => aircraft.id, { onDelete: "cascade" }),
	/** Master FOQA switch for the aircraft. OFF by default — when off, no debrief emails go
	 *  out for it (the feature doesn't exist for that tail). The aircraft-centric replacement
	 *  for per-person enrollment. */
	enabled: boolean("enabled").notNull().default(false),
	pullFromPolicy: boolean("pull_from_policy").notNull().default(true),
	createdAt,
	updatedAt,
});

export type FoqaAircraftSchema = typeof foqaAircraft.$inferSelect;
export type FoqaAircraftInsertSchema = typeof foqaAircraft.$inferInsert;

/**
 * Explicit (manual) FOQA debrief recipients for an aircraft — anyone who should receive its
 * debriefs without necessarily owning it (chief pilot, safety officer, ops, the CJP safety
 * committee, etc.), plus policyless aircraft. Policy-derived recipients are resolved live (see
 * `foqaAircraft.pullFromPolicy`) and are NOT stored here; the runtime resolver unions these
 * manual rows with the live policy users. Keyed by identity (the email). `label` is an optional
 * free-text role note (e.g. "Chief Pilot") shown in the console.
 */
export const foqaAircraftRecipients = pgTable(
	"foqa_aircraft_recipients",
	{
		aircraftId: uuid("aircraft_id")
			.references(() => aircraft.id, { onDelete: "cascade" })
			.notNull(),
		identityKey: text("identity_key")
			.references(() => identities.key, { onDelete: "cascade" })
			.notNull(),
		/**
		 * How this row entered: `manual` = an explicit FOQA recipient (always a recipient when
		 * enabled, removable); `policy` = an override row for a person resolved live from the
		 * policy — it only carries the enable/disable toggle (used to switch a policy recipient
		 * off without an asRole filter).
		 */
		source: text("source").$type<"manual" | "policy">().notNull().default("manual"),
		/** Per-recipient on/off. Manual recipients default on; a policy override row defaults to
		 *  whatever it's set to (a row only exists once toggled). */
		enabled: boolean("enabled").notNull().default(true),
		/** Optional role/label for why this person receives debriefs (free text). */
		label: text("label"),
		/** Staff user who added the link (audit). */
		createdBy: uuid("created_by").references(() => users.id, { onDelete: "set null" }),
		createdAt,
	},
	(table) => [
		primaryKey({ columns: [table.aircraftId, table.identityKey] }),
		index("foqa_aircraft_recipients_aircraft_id_idx").on(table.aircraftId),
		index("foqa_aircraft_recipients_identity_key_idx").on(table.identityKey),
	],
);

export type FoqaAircraftRecipientSchema = typeof foqaAircraftRecipients.$inferSelect;
export type FoqaAircraftRecipientInsertSchema = typeof foqaAircraftRecipients.$inferInsert;

/** Snapshot a current debrief row into a revision insert (the prior state, before a
 *  rewrite). Used by both the worker reprocess and the sms-api edit so history capture
 *  is identical on both paths. */
export const debriefToRevision = (
	d: FoqaDebriefSchema,
): FoqaDebriefRevisionInsertSchema => ({
	debriefId: d.id,
	flightId: d.flightId,
	version: d.version,
	source: d.source,
	score: d.score,
	safetyEventCount: d.safetyEventCount,
	vref: d.vref,
	computedVref: d.computedVref,
	performanceTableId: d.performanceTableId,
	approachType: d.approachType,
	overrides: d.overrides,
	summary: d.summary,
	measurements: d.measurements,
	editedBy: d.editedBy,
});

export const foqaExceedances = pgTable(
	"foqa_exceedances",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		debriefId: uuid("debrief_id")
			.references(() => foqaDebriefs.id, { onDelete: "cascade" })
			.notNull(),
		flightId: uuid("flight_id")
			.references(() => flights.id, { onDelete: "cascade" })
			.notNull(),
		aircraftId: uuid("aircraft_id")
			.references(() => aircraft.id, { onDelete: "cascade" })
			.notNull(),
		parameterKey: text("parameter_key").notNull(),
		phase: text("phase").notNull(),
		band: foqaBand("band").notNull(),
		actualValue: doublePrecision("actual_value"),
		score: integer("score").notNull(),
		weight: doublePrecision("weight").notNull(),
		flightTime: timestamp("flight_time", { withTimezone: true, mode: "date" }),
		createdAt,
	},
	(table) => [
		index("foqa_exceedances_debrief_id_idx").on(table.debriefId),
		index("foqa_exceedances_flight_id_idx").on(table.flightId),
		index("foqa_exceedances_aircraft_id_idx").on(table.aircraftId),
		index("foqa_exceedances_band_idx").on(table.band),
		index("foqa_exceedances_flight_time_idx").on(table.flightTime),
	],
);

export type FoqaExceedanceSchema = typeof foqaExceedances.$inferSelect;
export type FoqaExceedanceInsertSchema = typeof foqaExceedances.$inferInsert;

/**
 * Cache of LLM-generated aggregate findings ("Wilbur insight"). Keyed by a hash of the
 * query descriptor plus a digest of the rollup numbers, so any new data invalidates the entry.
 */
export const foqaFindings = pgTable(
	"foqa_findings",
	{
		id: uuid().defaultRandom().primaryKey().notNull(),
		fleetId: uuid("fleet_id")
			.references(() => fleets.id, { onDelete: "cascade" })
			.notNull(),
		cacheKey: text("cache_key").notNull().unique(),
		headline: text("headline").notNull(),
		watch: text("watch").notNull(),
		suggestedAction: text("suggested_action").notNull(),
		model: text("model").notNull(),
		createdAt,
	},
	(table) => [index("foqa_findings_fleet_idx").on(table.fleetId)],
);

export type FoqaFindingSchema = typeof foqaFindings.$inferSelect;
export type FoqaFindingInsertSchema = typeof foqaFindings.$inferInsert;

export const fleetAircraftRelations = relations(fleetAircraft, ({ one }) => ({
	fleet: one(fleets, {
		fields: [fleetAircraft.fleetId],
		references: [fleets.id],
	}),
	aircraft: one(aircraft, {
		fields: [fleetAircraft.aircraftId],
		references: [aircraft.id],
	}),
}));

export const fleetMembersRelations = relations(fleetMembers, ({ one }) => ({
	fleet: one(fleets, {
		fields: [fleetMembers.fleetId],
		references: [fleets.id],
	}),
	user: one(users, {
		fields: [fleetMembers.userId],
		references: [users.id],
	}),
}));

export const tailClaimsRelations = relations(tailClaims, ({ one }) => ({
	fleet: one(fleets, {
		fields: [tailClaims.fleetId],
		references: [fleets.id],
	}),
	aircraft: one(aircraft, {
		fields: [tailClaims.aircraftId],
		references: [aircraft.id],
	}),
	requestedByUser: one(users, {
		fields: [tailClaims.requestedBy],
		references: [users.id],
	}),
	resolvedByUser: one(users, {
		fields: [tailClaims.resolvedBy],
		references: [users.id],
	}),
}));

export const tailPairingsRelations = relations(tailPairings, ({ one }) => ({
	aircraft: one(aircraft, {
		fields: [tailPairings.aircraftId],
		references: [aircraft.id],
	}),
	requestedByUser: one(users, {
		fields: [tailPairings.requestedBy],
		references: [users.id],
	}),
}));

export const foqaThresholdsRelations = relations(foqaThresholds, ({ one, many }) => ({
	createdByUser: one(users, {
		fields: [foqaThresholds.createdBy],
		references: [users.id],
	}),
	debriefs: many(foqaDebriefs),
}));

export const foqaDebriefsRelations = relations(foqaDebriefs, ({ one, many }) => ({
	flight: one(flights, {
		fields: [foqaDebriefs.flightId],
		references: [flights.id],
	}),
	aircraft: one(aircraft, {
		fields: [foqaDebriefs.aircraftId],
		references: [aircraft.id],
	}),
	threshold: one(foqaThresholds, {
		fields: [foqaDebriefs.thresholdId],
		references: [foqaThresholds.id],
	}),
	exceedances: many(foqaExceedances),
}));

export const foqaExceedancesRelations = relations(foqaExceedances, ({ one }) => ({
	debrief: one(foqaDebriefs, {
		fields: [foqaExceedances.debriefId],
		references: [foqaDebriefs.id],
	}),
	flight: one(flights, {
		fields: [foqaExceedances.flightId],
		references: [flights.id],
	}),
	aircraft: one(aircraft, {
		fields: [foqaExceedances.aircraftId],
		references: [aircraft.id],
	}),
}));

export const garminConnectionStatus = pgEnum("garmin_connection_status", [
	"connected",
	"revoked",
]);

/**
 * A Garmin Mutual Customer Identifier and the account that claimed it. Garmin mints one MCI
 * per Garmin user/org and an aircraft sits under at most one MCI at a time, so the MCI is
 * consent bookkeeping — logs route by tail, which is unique on its own.
 */
export const garminCustomers = pgTable("garmin_customers", {
	mci: text("mci").primaryKey().notNull(),
	/** Whoever completed the browser handshake. */
	userId: uuid("user_id").references(() => users.id, { onDelete: "set null" }),
	status: garminConnectionStatus("status").notNull().default("connected"),
	connectedAt: timestamp("connected_at", { mode: "date", withTimezone: true }),
	revokedAt: timestamp("revoked_at", { mode: "date", withTimezone: true }),
	/** Null while a Garmin signup waits for staff review; its aircraft are not enrolled until set. */
	approvedAt: timestamp("approved_at", { mode: "date", withTimezone: true }),
	approvedBy: uuid("approved_by").references(() => users.id, { onDelete: "set null" }),
	createdAt,
	updatedAt,
});

export type GarminCustomerSchema = typeof garminCustomers.$inferSelect;
export type GarminCustomerInsertSchema = typeof garminCustomers.$inferInsert;

/**
 * Per-tail authorization state, driven by Garmin's notification queue. A null `aircraftId`
 * is a parked tail: authorized on Garmin's side but not onboarded here, so its logs are
 * skipped rather than fetched.
 */
export const garminAircraftAuthorizations = pgTable(
	"garmin_aircraft_authorizations",
	{
		mci: text("mci")
			.references(() => garminCustomers.mci, { onDelete: "cascade" })
			.notNull(),
		tailNumber: text("tail_number").notNull(),
		authorized: boolean("authorized").notNull().default(true),
		aircraftId: uuid("aircraft_id").references(() => aircraft.id, { onDelete: "set null" }),
		grantedAt: timestamp("granted_at", { mode: "date", withTimezone: true }),
		revokedAt: timestamp("revoked_at", { mode: "date", withTimezone: true }),
		createdAt,
		updatedAt,
	},
	(table) => [
		primaryKey({ columns: [table.mci, table.tailNumber] }),
		index("garmin_aircraft_authorizations_tail_idx").on(table.tailNumber),
		index("garmin_aircraft_authorizations_aircraft_id_idx").on(table.aircraftId),
	],
);

export type GarminAircraftAuthorizationSchema = typeof garminAircraftAuthorizations.$inferSelect;

export const garminQueue = pgEnum("garmin_queue", [
	"flight_logs",
	"auth_notifications",
]);

/**
 * Our head pointer into Garmin's reference queues — they keep no server-side cursor.
 * Records age out of their queues after two weeks, so `lastPolledAt` falling behind is a
 * data-loss clock, not just a staleness signal.
 */
export const garminCursors = pgTable("garmin_cursors", {
	queue: garminQueue("queue").primaryKey().notNull(),
	previousId: text("previous_id"),
	lastPolledAt: timestamp("last_polled_at", { mode: "date", withTimezone: true }),
	createdAt,
	updatedAt,
});

export type GarminCursorSchema = typeof garminCursors.$inferSelect;

export const mocoSnapshotOutcome = pgEnum("moco_snapshot_outcome", [
	"accepted",
	"stale",
	"invalid",
	"error",
]);

export const mocoOpportunities = pgTable("moco_opportunities", {
	id: uuid().defaultRandom().primaryKey().notNull(),
	opportunityId: text("opportunity_id").unique(),
	submissionId: uuid("submission_id").notNull().references(() => submissions.id, { onDelete: "cascade" }),
	/** Null until the submission binds; each policy is then its own Moco opportunity. */
	policyId: text("policy_id").references(() => policies.id, { onDelete: "cascade" }),
	lastVersion: timestamp("last_version", { mode: "date", withTimezone: true }).notNull(),
	lastOutcome: mocoSnapshotOutcome("last_outcome").notNull(),
	lastSnapshotId: text("last_snapshot_id"),
	lastError: text("last_error"),
	lastSyncedAt: timestamp("last_synced_at", { mode: "date", withTimezone: true }).notNull(),
	createdAt,
	updatedAt,
}, (table) => [
	unique("moco_opportunities_submission_policy_unique").on(table.submissionId, table.policyId).nullsNotDistinct(),
]);

export type MocoOpportunitySchema = typeof mocoOpportunities.$inferSelect;

export const mocoOpportunitiesRelations = relations(mocoOpportunities, ({ one }) => ({
	submission: one(submissions, {
		fields: [mocoOpportunities.submissionId],
		references: [submissions.id],
	}),
}));

export const garminLogStatus = pgEnum("garmin_log_status", [
	"created",
	"updated",
	"deleted",
]);

/**
 * Garmin re-emits a log as UPDATED when disjoint segments merge, DELETED when it is folded
 * into another, and replays CREATED for logs we already hold whenever an aircraft is
 * re-authorized — so processing is keyed on (uuid, version), not on arrival.
 */
export const garminLogs = pgTable(
	"garmin_logs",
	{
		flightLogUuid: text("flight_log_uuid").primaryKey().notNull(),
		/** Timestamp-shaped version string from the reference. */
		version: text("version").notNull(),
		status: garminLogStatus("status").notNull(),
		/** Newest reference id — the handle the file endpoint takes. */
		referenceId: text("reference_id").notNull(),
		tailNumber: text("tail_number").notNull(),
		mci: text("mci"),
		aircraftId: uuid("aircraft_id").references(() => aircraft.id, { onDelete: "set null" }),
		flightId: uuid("flight_id").references(() => flights.id, { onDelete: "set null" }),
		/** Cloudstore key for the CSV; null when the log was parked rather than fetched. */
		storePath: text("store_path"),
		/** Set once the log has been fetched, matched and published for scoring. */
		processedAt: timestamp("processed_at", { mode: "date", withTimezone: true }),
		/** Why a log was not processed — e.g. an authorized tail we have no aircraft for. */
		parkedReason: text("parked_reason"),
		createdAt,
		updatedAt,
	},
	(table) => [
		index("garmin_logs_tail_idx").on(table.tailNumber),
		index("garmin_logs_flight_id_idx").on(table.flightId),
	],
);

export type GarminLogSchema = typeof garminLogs.$inferSelect;
export type GarminLogInsertSchema = typeof garminLogs.$inferInsert;

export const garminLogsRelations = relations(garminLogs, ({ one }) => ({
	flight: one(flights, {
		fields: [garminLogs.flightId],
		references: [flights.id],
	}),
	aircraft: one(aircraft, {
		fields: [garminLogs.aircraftId],
		references: [aircraft.id],
	}),
}));

export const garminCustomersRelations = relations(garminCustomers, ({ one, many }) => ({
	user: one(users, {
		fields: [garminCustomers.userId],
		references: [users.id],
	}),
	aircraftAuthorizations: many(garminAircraftAuthorizations),
}));

export const garminAircraftAuthorizationsRelations = relations(
	garminAircraftAuthorizations,
	({ one }) => ({
		customer: one(garminCustomers, {
			fields: [garminAircraftAuthorizations.mci],
			references: [garminCustomers.mci],
		}),
		aircraft: one(aircraft, {
			fields: [garminAircraftAuthorizations.aircraftId],
			references: [aircraft.id],
		}),
	}),
);

export const llmTokenUsage = pgTable(
	"llm_token_usage",
	{
		day: date("day", { mode: "string" }).notNull(),
		functionId: text("function_id").notNull(),
		provider: text("provider").notNull(),
		model: text("model").notNull(),
		calls: integer("calls").notNull(),
		inputTokens: bigint("input_tokens", { mode: "number" }).notNull(),
		outputTokens: bigint("output_tokens", { mode: "number" }).notNull(),
	},
	(table) => [primaryKey({ columns: [table.day, table.functionId, table.provider, table.model] })],
);

export type LlmTokenUsageInsertSchema = typeof llmTokenUsage.$inferInsert;

export const demoAircraft = pgTable(
	"demo_aircraft",
	{
		userId: uuid("user_id")
			.references(() => users.id, { onDelete: "cascade" })
			.notNull(),
		aircraftId: uuid("aircraft_id")
			.references(() => aircraft.id, { onDelete: "cascade" })
			.notNull(),
		createdAt,
	},
	(table) => [
		primaryKey({ columns: [table.userId, table.aircraftId] }),
		index("demo_aircraft_aircraft_id_idx").on(table.aircraftId),
	],
);
